import { createContext, useContext, useEffect, useMemo, useState } from "react";
import "@/App.css";
import {
  Search, Gem, ShieldCheck, Sparkles, ScanLine, X, Send, ShoppingCart, Lock, AlertTriangle,
  BadgeCheck, Package, Scale, Ruler, MapPin, User, Store, Building2, Upload, CheckCircle2, Gavel, Globe2, LogIn,
  Heart, Flame, Timer, TrendingUp, Inbox, Video, Play, Camera, ImagePlus, Link2, Bell, Printer, ZoomIn, FlaskConical, Hexagon,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800";
const onImgError = (e) => {
  if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errMsg = (d) => {
  if (!d) return "Errore imprevisto. Riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg.replace(/^Value error, /, "") : JSON.stringify(e))).join(" ");
  return d.msg || String(d);
};
const apiFetch = async (path, { method = "GET", body, token } = {}) => {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(errMsg(data.detail));
  return data;
};
const AuthCtx = createContext({ user: null, token: "", openLogin: () => {}, logout: () => {} });
const useAuth = () => useContext(AuthCtx);
const RUOLO_TO_ROLE = { Venditore: "seller", Venditore_Privato: "seller", Acquirente_B2C: "b2c", Acquirente_B2B: "b2b" };
const SELLER_RUOLI = ["Venditore", "Venditore_Privato"];
const canSellUser = (u) => !!u && (SELLER_RUOLI.includes(u.ruolo) || !!u.can_sell || !!u.is_admin);
const buyerRoleOf = (u) => (u?.ruolo === "Acquirente_B2B" || (SELLER_RUOLI.includes(u?.ruolo) && (u?.partita_iva || "").trim()) ? "b2b" : "b2c");
const profileLabel = (u) => `${u.is_admin ? "Admin · " : ""}Acquirente ${buyerRoleOf(u) === "b2b" ? "B2B" : "B2C"}${canSellUser(u) ? " + Venditore" : ""}`;
const SHIP = { nazionale: "Nazionale", europa: "Europa", mondo: "Resto del Mondo" };
const SHIP_DEFAULT = { spedizione_nazionale: 15, spedizione_europa: 35, spedizione_mondo: 70 };
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const quote = (prezzo, sped, metodo) => {
  const p = Number(prezzo) || 0;
  const fee = Math.max(5, r2(p * (metodo === "carta" ? 0.05 : 0.03)));
  const comm = Math.max(5, r2(p * 0.05));
  return { fee, totale: r2(p + (Number(sped) || 0) + fee), comm, netto: r2(p - comm) };
};
const sellerAlias = (p) => (/^Seller #KH-/.test(p?.venditore_codice || "") ? p.venditore_codice : `Seller #KH-${String(p?.venditore_id || p?.id || "").replace(/-/g, "").slice(0, 4).toUpperCase() || "ANON"}`);
const fromApi = (m) => ({
  ...SHIP_DEFAULT, ...m, origine: m.miniera, img: m.foto_url, video: m.video_url,
  ai_score: `${m.purezza || "99%"} Naturale - Scheda AI preliminare`, verificato: true, pezzo_unico: true,
});
const toApi = (p) => ({
  titolo: p.titolo, specie: p.specie, miniera: p.origine || "", nazione: p.nazione || "", purezza: p.purezza || "99%",
  peso_g: Number(p.peso_g) || 0, dimensioni_mm: p.dimensioni_mm || "", prezzo_b2c: Number(p.prezzo_b2c) || 0,
  prezzo_b2b_kg: Number(p.prezzo_b2b_kg) || 0, moq_kg: Number(p.moq_kg) || 0, foto_url: p.img === FALLBACK_IMG ? "" : p.img || "",
  video_url: p.video || "", descrizione: p.descrizione || "", categoria: p.categoria || "", proprieta: p.proprieta || "", certificazione: p.certificazione || "Nessuna", formula: p.formula || "", sistema_cristallino: p.sistema_cristallino || "", durezza: p.durezza || "", lucentezza: p.lucentezza || "", images: (p.images || []).slice(0, 4), traduzioni: p.traduzioni || {},
  spedizione_nazionale: Number(p.spedizione_nazionale) || 0, spedizione_europa: Number(p.spedizione_europa) || 0, spedizione_mondo: Number(p.spedizione_mondo) || 0,
});
const fmt2 = (eur, cur) => new Intl.NumberFormat("it-IT", { style: "currency", currency: cur, minimumFractionDigits: 2 }).format((cur === "USD" ? eur * 1.08 : eur) || 0);

const AI_SCORE = "99% Naturale - Scheda AI preliminare";
const SEED_PRODUCTS = [
  { id: "kh-001", titolo: "Drusa Ametista Imperiale", specie: "Ametista", origine: "Uruguay - Artigas", nazione: "Uruguay", venditore_codice: "Seller #KH-ED61", verificato: true, peso_g: 3200, dimensioni_mm: "240 x 180 x 150", prezzo_b2c: 450, prezzo_b2b_kg: 85, moq_kg: 20, img: "https://images.unsplash.com/photo-1627768919962-fb6af351759c?w=800", ai_score: AI_SCORE, pezzo_unico: true, descrizione: "Drusa di ametista dal viola profondo con cristalli scettrati e lucentezza vitrea intensa. Geode estratto a mano nelle colate basaltiche di Artigas." },
  { id: "kh-002", titolo: "Tormalina Paraíba su Quarzo", specie: "Tormalina", origine: "Brasile - Minas Gerais", nazione: "Brasile", venditore_codice: "Seller #KH-ED61", verificato: true, peso_g: 180, dimensioni_mm: "65 x 40 x 32", prezzo_b2c: 1200, prezzo_b2b_kg: 950, moq_kg: 2, img: "https://images.unsplash.com/photo-1546608135-e5de34abc308?w=800", ai_score: AI_SCORE, pezzo_unico: true, descrizione: "Cristallo di tormalina color turchese neon su matrice di quarzo ialino. Colorazione dovuta a tracce di rame, terminazione naturale integra." },
  { id: "kh-003", titolo: "Azzurrite Cristallizzata da Museo", specie: "Azzurrite", origine: "Marocco - Touissit", nazione: "Marocco", venditore_codice: "Seller #KH-ED61", verificato: true, peso_g: 420, dimensioni_mm: "90 x 70 x 45", prezzo_b2c: 320, prezzo_b2b_kg: 140, moq_kg: 10, img: "https://images.unsplash.com/photo-1596226310268-f76b50d14a4a?w=800", ai_score: AI_SCORE, pezzo_unico: true, descrizione: "Cristalli tabulari di azzurrite blu notte con lucentezza adamantina, qualità da collezione museale dalla storica miniera di Touissit." },
  { id: "kh-004", titolo: "Quarzo Fumé Gwindel Alpino", specie: "Quarzo", origine: "Alpi Svizzere", nazione: "Svizzera", venditore_codice: "Seller #KH-ED61", verificato: true, peso_g: 260, dimensioni_mm: "78 x 52 x 30", prezzo_b2c: 280, prezzo_b2b_kg: 110, moq_kg: 5, img: "https://images.unsplash.com/photo-1562162135-9f64f33e623b?w=800", ai_score: AI_SCORE, pezzo_unico: true, descrizione: "Raro gwindel di quarzo fumé con crescita elicoidale, recuperato da cristalliere alpine ad alta quota. Trasparenza eccellente." },
  { id: "kh-005", titolo: "Lotto Fluorite Verde Ottaedrica", specie: "Fluorite", origine: "Pakistan - Gilgit", nazione: "Pakistan", venditore_codice: "Seller #KH-ED61", verificato: true, peso_g: 1000, dimensioni_mm: "30-55 (per pezzo)", prezzo_b2c: 95, prezzo_b2b_kg: 35, moq_kg: 50, img: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800", ai_score: AI_SCORE, pezzo_unico: true, descrizione: "Lotto di ottaedri di fluorite verde menta a sfaldatura naturale, ideali per rivendita, cristalloterapia e gioielleria grezza." },
  { id: "kh-006", titolo: "Blocco Lapislazzuli Royal Blue", specie: "Lapislazzuli", origine: "Afghanistan - Badakhshan", nazione: "Afghanistan", venditore_codice: "Seller #KH-ED61", verificato: true, peso_g: 1500, dimensioni_mm: "140 x 95 x 70", prezzo_b2c: 190, prezzo_b2b_kg: 65, moq_kg: 25, img: "https://images.unsplash.com/photo-1599707367072-cd6ada2bc375?w=800", ai_score: AI_SCORE, pezzo_unico: true, descrizione: "Blocco grezzo di lapislazzuli blu reale con inclusioni di pirite dorata, dalla leggendaria miniera di Sar-e-Sang." },
];

const FORMULAS = [
  [["tormalina", "elbaite", "rubellite", "paraiba"], "Na(Li₁.₅Al₁.₅)Al₆(Si₆O₁₈)(BO₃)₃(OH)₃(OH)", "Trigonale"],
  [["smeraldo", "berillo", "emerald"], "Be₃Al₂Si₆O₁₈", "Esagonale"], [["fluorite"], "CaF₂", "Cubico (Isometrico)"], [["azzurrite"], "Cu₃(CO₃)₂(OH)₂", "Monoclino"],
  [["malachite"], "Cu₂(CO₃)(OH)₂", "Monoclino"], [["lapislazzuli", "lazurite"], "Na₇Ca(Al₆Si₆O₂₄)(SO₄)(S₃)·H₂O", "Cubico (Lazurite)"], [["pirite"], "FeS₂", "Cubico (Isometrico)"],
  [["rodocrosite"], "MnCO₃", "Trigonale"], [["quarzo", "ametista", "citrino", "gwindel"], "SiO₂", "Trigonale"],
];
const mineralOf = (p) => {
  if (p.formula) return [null, p.formula, p.sistema_cristallino || "—"];
  const t = `${p.specie || ""} | ${p.titolo || ""}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  for (const part of t.split("|")) {
    const hit = FORMULAS.find(([ws]) => ws.some((w) => part.includes(w)));
    if (hit) return hit;
  }
  return null;
};
const formulaOf = (p) => mineralOf(p)?.[1] || "";

const H = 3600000;
const seedAuctions = () => {
  const n = Date.now();
  return {
    "kh-001": { endsAt: n + 3 * H + 42 * 60000 + 18000, bid: 380, bids: 14 },
    "kh-002": { endsAt: n + 1 * H + 15 * 60000 + 40000, bid: 1040, bids: 27 },
    "kh-004": { endsAt: n + 7 * H + 5 * 60000 + 9000, bid: 215, bids: 9 },
  };
};
const SEED_OFFERS = [
  { productId: "kh-002", amount: 1050, cliente: "Collector_Milano_#21", role: "b2c", at: 1, stato: "In attesa" },
  { productId: "kh-006", amount: 1450, cliente: "GemWholesale_HK_#05 (25 Kg)", role: "b2b", at: 2, stato: "In attesa" },
];

const ROLES = {
  b2c: { label: "Collezionista B2C", icon: User, mode: "b2c" },
  b2b: { label: "Grossista B2B", icon: Building2, mode: "b2b" },
  seller: { label: "Venditore Verificato", icon: Store, mode: "b2c" },
};
const BLOCK_MSG = "Sicurezza KristallHub: per la tua tutela Escrow, è vietato scambiare contatti personali prima dell'ordine.";
const BLOCK_WORDS = /(whatsapp|telefono|e-?mail|\bmail\b|instagram|telegram|contattami)/i;
const isBlocked = (t) => /\d{5,}/.test(t.replace(/[\s.\-()+/]/g, "")) || t.includes("@") || BLOCK_WORDS.test(t);

const load = (key, fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    if (v === null || v === undefined) return fallback;
    if (Array.isArray(fallback) && (!Array.isArray(v) || v.length === 0)) return fallback;
    return v;
  } catch {
    return fallback;
  }
};
const usePersist = (key, fallback) => {
  const [v, setV] = useState(() => load(key, fallback));
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(v)); } catch { console.warn(`localStorage pieno: ${key}`); }
  }, [key, v]);
  return [v, setV];
};

const compressImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const im = new Image();
    im.onerror = () => resolve(reader.result);
    im.onload = () => {
      const s = Math.min(1, 800 / Math.max(im.width, im.height));
      const c = document.createElement("canvas");
      c.width = Math.round(im.width * s);
      c.height = Math.round(im.height * s);
      c.getContext("2d").drawImage(im, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.7));
    };
    im.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const PHOTO_SLOTS = ["Frontale Principale", "Macro Cristalli/Punte", "Retro/Matrice", "Scala Dimensionale o Lotto B2B"];
const DEMO_VIDEO = "demo:360";
const galleryOf = (p) => {
  if (Array.isArray(p.images) && p.images.length) return p.images;
  if (!p.img || !p.img.includes("unsplash.com")) return [p.img || FALLBACK_IMG];
  const base = p.img.split("?")[0];
  return [
    p.img,
    `${base}?w=800&h=600&fit=crop&crop=focalpoint&fp-x=0.5&fp-y=0.45&fp-z=2.2`,
    `${base}?w=800&h=600&fit=crop&crop=focalpoint&fp-x=0.25&fp-y=0.65&fp-z=1.6`,
    `${base}?w=800&h=600&fit=crop&crop=focalpoint&fp-x=0.75&fp-y=0.35&fp-z=1.4`,
  ];
};
const parseVideo = (url) => {
  if (!url || url === DEMO_VIDEO) return { type: "demo" };
  const yt = url.match(/(?:youtu\.be\/|v=|shorts\/|embed\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&loop=1` };
  const ig = url.match(/instagram\.com\/(?:reel|p)\/([\w-]+)/);
  if (ig) return { type: "iframe", src: `https://www.instagram.com/reel/${ig[1]}/embed` };
  if (/\.(mp4|webm|mov|ogg)(\?|$)/i.test(url)) return { type: "video", src: url };
  return { type: "link", src: url };
};

function VideoPlayer({ url, poster }) {
  const v = parseVideo(url);
  return (
    <div className="relative rounded-xl overflow-hidden border border-[#10b981]/40 bg-black aspect-video" data-testid="video-player">
      <span className="absolute top-3 left-3 z-10 flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#10b981] text-black" data-testid="video-verified-badge">
        <ShieldCheck className="w-3 h-3" />Video verificato senza filtri
      </span>
      {v.type === "demo" && (
        <div className="w-full h-full flex items-center justify-center [perspective:900px]">
          <Img src={poster} alt="360" className="kh-spin h-4/5 aspect-square object-cover rounded-full shadow-[0_0_60px_rgba(212,175,55,0.35)]" />
          <div className="kh-light absolute inset-0 pointer-events-none" />
          <span className="absolute bottom-3 right-3 text-[10px] text-zinc-400">Demo 360° · Luce Reale</span>
        </div>
      )}
      {v.type === "iframe" && <iframe title="video-360" src={v.src} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />}
      {v.type === "video" && <video src={v.src} className="w-full h-full object-contain" controls autoPlay muted loop playsInline />}
      {v.type === "link" && (
        <a href={v.src} target="_blank" rel="noopener noreferrer" className="w-full h-full flex items-center justify-center gap-2 text-[#d4af37] underline" data-testid="video-external-link">
          <Play className="w-5 h-5" />Apri video 360° esterno
        </a>
      )}
    </div>
  );
}

function MediaGallery({ product, onClose }) {
  const imgs = galleryOf(product);
  const [active, setActive] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [lens, setLens] = useState(null);
  const moveLens = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setLens({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  return (
    <div className="p-4 sm:p-6 space-y-4 bg-black">
      <div className="relative rounded-2xl overflow-hidden">
        <div className="overflow-hidden cursor-zoom-in" onMouseMove={moveLens} onMouseLeave={() => setLens(null)} data-testid="detail-loupe">
          <Img src={imgs[active] || imgs[0]} alt={product.titolo} className="w-full h-72 lg:h-[480px] object-cover transition-transform duration-150 ease-out" testid="detail-image"
            style={lens ? { transform: "scale(2.5)", transformOrigin: `${lens.x}% ${lens.y}%` } : undefined} />
        </div>
        <span className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full bg-black/70 text-[#d4af37] border border-[#d4af37]/40">
          <ZoomIn className="w-3 h-3" />{lens ? "Lente 2.5x attiva" : "Passa il mouse: Lente 2.5x"}
        </span>
        <button onClick={onClose} data-testid="detail-close-btn" className="absolute top-4 right-4 lg:hidden bg-black/60 rounded-full p-2"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {imgs.slice(0, 4).map((src, i) => (
          <button key={i} onClick={() => { setActive(i); setShowVideo(false); }} data-testid={`gallery-thumb-${i}`} title={PHOTO_SLOTS[i]}
            className={`rounded-lg overflow-hidden border-2 transition-colors ${active === i ? "border-[#d4af37]" : "border-transparent hover:border-white/30"}`}>
            <Img src={src} alt={PHOTO_SLOTS[i]} className="w-full aspect-square object-cover" />
          </button>
        ))}
      </div>
      <button onClick={() => setShowVideo((s) => !s)} data-testid="play-video-btn"
        className="w-full py-3 rounded-full border border-[#10b981]/60 text-[#10b981] font-semibold flex items-center justify-center gap-2 hover:bg-[#10b981]/10 transition-colors">
        <Video className="w-5 h-5" />{showVideo ? "Chiudi Video" : "Riproduci Video 360° (Luce Reale)"}
      </button>
      {showVideo && <VideoPlayer url={product.video || DEMO_VIDEO} poster={imgs[0]} />}
    </div>
  );
}

function PhotoUploader({ photos, setPhotos, onFirstPhoto }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const addFiles = async (files, slot) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setBusy(true);
    try {
      const data = await Promise.all(list.slice(0, 4).map(compressImage));
      const next = [...photos];
      while (next.length < 4) next.push(null);
      let firstName = "";
      if (slot !== undefined) { next[slot] = data[0]; if (slot === 0) firstName = list[0].name; }
      else data.forEach((d, k) => { const i = next.findIndex((x) => !x); if (i !== -1) { next[i] = d; if (i === 0) firstName = list[k].name; } });
      setPhotos(next);
      if (next[0] && next[0] !== photos[0]) onFirstPhoto(next[0], firstName);
    } catch {
      console.warn("Errore compressione immagine");
    }
    setBusy(false);
  };
  const remove = (i) => setPhotos(photos.map((p, j) => (j === i ? null : p)));
  return (
    <div className="space-y-3 mb-6">
      <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        className={`block cursor-pointer rounded-xl border-2 border-dashed p-5 text-center transition-colors ${drag ? "border-[#10b981] bg-[#10b981]/10" : "border-[#d4af37]/40 hover:border-[#d4af37]"}`}
        data-testid="photo-dropzone">
        <input type="file" accept="image/*,.heic,.heif,.webp,.avif,.bmp,.tiff,.tif,.jfif,.gif,.png,.jpg,.jpeg" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="photo-file-input" />
        <ImagePlus className="w-8 h-8 mx-auto mb-2 text-[#d4af37]" />
        <p className="text-sm font-medium">{busy ? "Compressione in corso..." : "Trascina o carica fino a 4 foto"}</p>
        <p className="text-[11px] text-zinc-500 mt-1">Ridimensionate a 800px · JPEG 70% automatico</p>
      </label>
      <div className="grid grid-cols-2 gap-2">
        {PHOTO_SLOTS.map((label, i) => (
          <div key={label} className="relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-black" data-testid={`photo-slot-${i}`}>
            {photos[i] ? (
              <>
                <Img src={photos[i]} alt={label} className="w-full h-full object-cover" />
                <button onClick={() => remove(i)} data-testid={`photo-remove-${i}`} className="absolute top-1 right-1 bg-black/70 rounded-full p-1"><X className="w-3 h-3" /></button>
              </>
            ) : (
              <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer text-zinc-500 hover:text-[#d4af37] transition-colors p-2 text-center">
                <input type="file" accept="image/*,.heic,.heif,.webp,.avif,.bmp,.tiff,.tif,.jfif,.gif,.png,.jpg,.jpeg" className="hidden" onChange={(e) => { addFiles(e.target.files, i); e.target.value = ""; }} data-testid={`photo-slot-input-${i}`} />
                <Camera className="w-5 h-5 mb-1" />
              </label>
            )}
            <span className="absolute bottom-0 inset-x-0 bg-black/75 text-[10px] text-center py-1 pointer-events-none">{i + 1}) {label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const fmt = (eur, cur) => {
  const val = cur === "USD" ? eur * 1.08 : eur;
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(val || 0);
};

const Img = ({ src, alt, className, testid, style }) => (
  <img src={src || FALLBACK_IMG} alt={alt} className={className} style={style} onError={onImgError} data-testid={testid} loading="lazy" />
);

function ModeSwitch({ mode, setMode }) {
  return (
    <div className="relative flex rounded-full bg-black p-1 border border-[#d4af37]/40 shadow-[0_0_24px_rgba(212,175,55,0.25)]" data-testid="mode-switch">
      <span className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full transition-transform duration-300 ${mode === "b2c" ? "translate-x-0 bg-[#d4af37] shadow-[0_0_18px_#d4af37]" : "translate-x-full bg-[#10b981] shadow-[0_0_18px_#10b981]"}`} />
      {[["b2c", "Collezionisti B2C (Pezzo Singolo)"], ["b2b", "Grossisti B2B (Lotti al Kg)"]].map(([k, l]) => (
        <button key={k} onClick={() => setMode(k)} data-testid={`mode-switch-${k}`}
          className={`relative z-10 w-1/2 px-3 sm:px-5 py-2 text-[11px] sm:text-xs font-semibold rounded-full whitespace-nowrap transition-colors ${mode === k ? "text-[#0f1115]" : "text-zinc-400 hover:text-white"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

const REG_EMPTY = { ruolo: "Acquirente_B2C", nome_ragione_sociale: "", email: "", password: "", indirizzo_completo: "", nazione: "Italia", codice_fiscale: "", partita_iva: "" };
const REG_FIELDS = [
  ["nome_ragione_sociale", "Nome e Cognome / Ragione Sociale"], ["email", "Email"], ["password", "Password (min 8 caratteri)"],
  ["indirizzo_completo", "Indirizzo completo di spedizione"], ["nazione", "Nazione"], ["codice_fiscale", "Codice Fiscale"],
];
const COUNTRY_CODES = "AF AX AL DZ AS AD AO AI AQ AG AR AM AW AU AT AZ BS BH BD BB BY BE BZ BJ BM BT BO BQ BA BW BV BR IO BN BG BF BI CV KH CM CA KY CF TD CL CN CX CC CO KM CG CD CK CR CI HR CU CW CY CZ DK DJ DM DO EC EG SV GQ ER EE SZ ET FK FO FJ FI FR GF PF TF GA GM GE DE GH GI GR GL GD GP GU GT GG GN GW GY HT HM VA HN HK HU IS IN ID IR IQ IE IM IL IT JM JP JE JO KZ KE KI KP KR XK KW KG LA LV LB LS LR LY LI LT LU MO MG MW MY MV ML MT MH MQ MR MU YT MX FM MD MC MN ME MS MA MZ MM NA NR NP NL NC NZ NI NE NG NU NF MK MP NO OM PK PW PS PA PG PY PE PH PN PL PT PR QA RE RO RU RW BL SH KN LC MF PM VC WS SM ST SA SN RS SC SL SG SX SK SI SB SO ZA GS SS ES LK SD SR SJ SE CH SY TW TJ TZ TH TL TG TK TO TT TN TR TM TC TV UG UA AE GB US UM UY UZ VU VE VN VG VI WF EH YE ZM ZW".split(" ");
const COUNTRIES = (() => {
  try {
    const dn = new Intl.DisplayNames(["it"], { type: "region" });
    return [...new Set(COUNTRY_CODES.map((c) => dn.of(c)))].sort((a, b) => a.localeCompare(b, "it"));
  } catch { return ["Italia"]; }
})();
const inputCls = "mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#d4af37]";
const errCls = (bad) => (bad ? " !border-red-500 focus:!border-red-500" : "");
const Tip = ({ text, testid }) => (
  <span title={text} aria-label={text} data-testid={testid} className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-zinc-500/60 text-[10px] leading-none text-zinc-400 cursor-help hover:text-[#d4af37] hover:border-[#d4af37] align-middle ml-1">ⓘ</span>
);
const Help = ({ children }) => <span className="block mt-1 text-[11px] leading-snug text-zinc-500">{children}</span>;
const FieldErr = ({ msg, testid }) => (msg ? <span className="block mt-1 text-[11px] text-red-400" data-testid={testid}>{msg}</span> : null);
const REG_HELP = {
  nome_ragione_sociale: "Resta privato: sul sito apparirà solo il tuo codice anonimo Seller #KH-XXXX.",
  email: "Serve per accedere e per ricevere le comunicazioni sugli ordini. Non viene mai mostrata pubblicamente.",
  password: "Almeno 8 caratteri.",
  indirizzo_completo: "Via, numero civico, CAP, città: serve per le spedizioni e resta privato.",
  nazione: "Paese di residenza o della sede legale.",
  codice_fiscale: "Codice Fiscale italiano o Tax ID del tuo paese. Obbligatorio per tutti, resta privato.",
};
const REG_REQUIRED_MSG = {
  nome_ragione_sociale: "Inserisci nome e cognome o ragione sociale (almeno 2 caratteri).",
  email: "Inserisci un indirizzo email valido (es. nome@dominio.it).",
  password: "La password deve avere almeno 8 caratteri.",
  indirizzo_completo: "Inserisci l'indirizzo completo (almeno 5 caratteri).",
  nazione: "Seleziona la nazione.",
  codice_fiscale: "Inserisci il Codice Fiscale o Tax ID (almeno 5 caratteri).",
};
const validateReg = (f, tab, needPiva) => {
  const e = {};
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) e.email = REG_REQUIRED_MSG.email;
  if (tab === "login") { if (!f.password) e.password = "Inserisci la password."; return e; }
  if (f.password.length < 8) e.password = REG_REQUIRED_MSG.password;
  if (f.nome_ragione_sociale.trim().length < 2) e.nome_ragione_sociale = REG_REQUIRED_MSG.nome_ragione_sociale;
  if (f.indirizzo_completo.trim().length < 5) e.indirizzo_completo = REG_REQUIRED_MSG.indirizzo_completo;
  if (!f.nazione) e.nazione = REG_REQUIRED_MSG.nazione;
  if (f.codice_fiscale.trim().length < 5) e.codice_fiscale = REG_REQUIRED_MSG.codice_fiscale;
  if (needPiva && !f.partita_iva.trim()) e.partita_iva = "La Partita IVA è obbligatoria per Venditore Professionale e Acquirente B2B.";
  return e;
};

function AuthModal({ onAuth, onGuest, onClose }) {
  const [tab, setTab] = useState("login");
  const [f, setF] = useState(REG_EMPTY);
  const [err, setErr] = useState("");
  const [fe, setFe] = useState({});
  const [busy, setBusy] = useState(false);
  const needPiva = ["Venditore", "Acquirente_B2B"].includes(f.ruolo);
  const set = (k) => (e) => { setF((x) => ({ ...x, [k]: e.target.value })); setFe((x) => ({ ...x, [k]: "" })); };
  const submit = async (e) => {
    e.preventDefault();
    const v = validateReg(f, tab, needPiva);
    setFe(v);
    if (Object.keys(v).length) { setErr("Controlla i campi evidenziati in rosso."); return; }
    setBusy(true); setErr("");
    try {
      const body = tab === "login" ? { email: f.email, password: f.password } : { ...f, partita_iva: needPiva ? f.partita_iva : f.partita_iva || null };
      const res = await apiFetch(tab === "login" ? "/auth/login" : "/auth/register", { method: "POST", body });
      onAuth(res.token, res.user);
    } catch (x) { setErr(x.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="login-modal">
      <form noValidate onSubmit={submit} className="bg-[#090a0f] border border-[#d4af37]/30 rounded-2xl p-8 w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-3xl text-[#d4af37]">{tab === "login" ? "Accedi" : "Crea il tuo profilo"}</h3>
          <button type="button" onClick={onClose} data-testid="login-modal-close"><X className="w-5 h-5 text-zinc-400 hover:text-white" /></button>
        </div>
        <div className="flex gap-2 mb-6">
          {[["login", "Accedi"], ["register", "Registrati"]].map(([k, l]) => (
            <button type="button" key={k} onClick={() => { setTab(k); setErr(""); setFe({}); }} data-testid={`auth-tab-${k}`}
              className={`flex-1 py-2 rounded-full text-sm border transition-colors ${tab === k ? "bg-[#d4af37] text-black border-[#d4af37]" : "border-white/10 text-zinc-300"}`}>{l}</button>
          ))}
        </div>
        {tab === "login" ? (
          <div className="space-y-3">
            <label className="block"><span className="text-xs text-zinc-500">Email</span><input type="email" value={f.email} onChange={set("email")} placeholder="es. nome@dominio.it" className={inputCls + errCls(fe.email)} data-testid="login-email" /><FieldErr msg={fe.email} testid="login-email-error" /></label>
            <label className="block"><span className="text-xs text-zinc-500">Password</span><input type="password" value={f.password} onChange={set("password")} className={inputCls + errCls(fe.password)} data-testid="login-password" /><FieldErr msg={fe.password} testid="login-password-error" /></label>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block sm:col-span-2"><span className="text-xs text-zinc-500">Ruolo / Tipo Venditore<Tip text="Scegli se vuoi vendere (come professionista o collezionista privato) oppure acquistare." /></span>
              <select value={f.ruolo} onChange={set("ruolo")} className={inputCls} data-testid="register-ruolo">
                <option value="Venditore">Venditore Professionale (B2B/P.IVA)</option>
                <option value="Venditore_Privato">Venditore Privato (Collezionista senza P.IVA)</option>
                <option value="Acquirente_B2C">Acquirente B2C (Collezionista)</option>
                <option value="Acquirente_B2B">Acquirente B2B (Grossista)</option>
              </select>
              <span className="block mt-2 text-[11px] leading-snug text-zinc-400 bg-black/60 border border-white/5 rounded-lg p-2.5" data-testid="register-ruolo-help">
                <b className="text-zinc-200">Venditore Professionale:</b> ditta o società con Partita IVA (obbligatoria). <b className="text-zinc-200">Collezionista Privato:</b> persona fisica che vende pezzi della propria collezione, serve solo il Codice Fiscale / Tax ID, senza P.IVA.
                <br /><Lock className="inline w-3 h-3 mr-1" />Il tuo nome reale resta privato: sul sito apparirà solo un codice anonimo <b className="text-[#d4af37]">Seller #KH-XXXX</b>, per tutelare la privacy e la sicurezza delle trattative.
              </span>
            </label>
            {REG_FIELDS.map(([k, l]) => k === "nazione" ? (
              <label key={k} className="block"><span className="text-xs text-zinc-500">{l} *</span>
                <select value={f.nazione} onChange={set("nazione")} className={inputCls + errCls(fe.nazione)} data-testid="register-nazione">
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Help>{REG_HELP.nazione}</Help><FieldErr msg={fe.nazione} testid="register-nazione-error" />
              </label>
            ) : (
              <label key={k} className={`block ${k === "indirizzo_completo" ? "sm:col-span-2" : ""}`}><span className="text-xs text-zinc-500">{l} *</span>
                <input type={k === "password" ? "password" : k === "email" ? "email" : "text"} value={f[k]} onChange={set(k)} className={inputCls + errCls(fe[k])} data-testid={`register-${k}`}
                  placeholder={{ nome_ragione_sociale: "es. Mario Rossi / Rossi Minerali S.r.l.", email: "es. nome@dominio.it", indirizzo_completo: "es. Via Roma 1, 20121 Milano (MI)", codice_fiscale: "es. RSSMRA80A01F205X" }[k]} />
                <Help>{REG_HELP[k]}</Help><FieldErr msg={fe[k]} testid={`register-${k}-error`} />
              </label>
            ))}
            <label className="block sm:col-span-2"><span className="text-xs text-zinc-500">Partita IVA {needPiva ? "* (obbligatoria)" : "(facoltativa)"}</span>
              <input value={f.partita_iva} onChange={set("partita_iva")} placeholder="es. IT01234567890" className={inputCls + errCls(fe.partita_iva)} data-testid="register-partita_iva" />
              <Help>Obbligatoria solo per Venditore Professionale e Acquirente B2B. I Collezionisti Privati possono lasciarla vuota.</Help><FieldErr msg={fe.partita_iva} testid="register-partita_iva-error" />
            </label>
            <p className="sm:col-span-2 text-[11px] text-zinc-500 flex items-center gap-1"><Lock className="w-3 h-3" />I dati fiscali restano privati e non vengono mai mostrati pubblicamente. Nessun dato bancario richiesto.</p>
          </div>
        )}
        {err && <p className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg p-3" data-testid="auth-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="auth-submit-btn" className="mt-6 w-full py-3 rounded-full bg-[#d4af37] text-black font-semibold disabled:opacity-50">
          {busy ? "Attendere..." : tab === "login" ? "Accedi" : "Registrati"}
        </button>
      </form>
    </div>
  );
}

function BecomeSeller({ big = false }) {
  const { token, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState("Venditore_Privato");
  const [val, setVal] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isPro = tipo === "Venditore";
  const submit = async (e) => {
    e.preventDefault();
    if (val.trim().length < 5) { setErr(isPro ? "Inserisci una Partita IVA valida (almeno 5 caratteri)." : "Inserisci il Codice Fiscale / Tax ID (almeno 5 caratteri)."); return; }
    setBusy(true); setErr("");
    try {
      const u = await apiFetch("/auth/become-seller", { method: "POST", token, body: { tipo, [isPro ? "partita_iva" : "codice_fiscale"]: val.trim() } });
      setUser(u); setOpen(false);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={big ? "become-seller-btn-dashboard" : "become-seller-btn"}
        className={`${big ? "mx-auto text-sm px-6 py-3" : "text-xs px-3 py-2"} rounded-full bg-[#10b981] text-black font-semibold hover:brightness-110 transition-[filter] flex items-center gap-1`}>
        <Store className="w-3.5 h-3.5" /> {big ? "Abilita profilo venditore / Diventa Venditore" : "Diventa Venditore"}
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form noValidate onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#090a0f] border border-[#d4af37]/30 rounded-2xl p-6 w-full max-w-md space-y-4" data-testid="become-seller-modal">
            <h3 className="font-display text-2xl">Diventa Venditore</h3>
            <p className="text-xs text-zinc-400">Il tuo account resta lo stesso e mantieni tutte le funzioni da acquirente (ordini, offerte, preferiti): non serve una nuova registrazione. Sul sito apparirai solo con il codice anonimo Seller #KH-XXXX.</p>
            <div className="grid grid-cols-2 gap-2">
              {[["Venditore_Privato", "Collezionista Privato", "Solo Codice Fiscale / Tax ID"], ["Venditore", "Professionista", "Con Partita IVA"]].map(([k, l, d]) => (
                <button type="button" key={k} onClick={() => { setTipo(k); setVal(""); setErr(""); }} data-testid={`become-seller-tipo-${k}`}
                  className={`text-left rounded-xl border p-3 text-sm transition-colors ${tipo === k ? "border-[#d4af37] bg-[#d4af37]/10" : "border-white/10"}`}>
                  <div className="font-semibold">{l}</div><div className="text-[11px] text-zinc-500">{d}</div>
                </button>
              ))}
            </div>
            <label className="block"><span className="text-xs text-zinc-500">{isPro ? "Partita IVA *" : "Codice Fiscale / Tax ID *"}</span>
              <input value={val} onChange={(e) => { setVal(e.target.value); setErr(""); }} placeholder={isPro ? "es. IT01234567890" : "es. RSSMRA80A01F205X"} data-testid="become-seller-input"
                className={inputCls + errCls(err)} />
              <FieldErr msg={err} testid="become-seller-error" />
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 py-2.5 rounded-full border border-white/15 text-sm">Annulla</button>
              <button type="submit" disabled={busy} data-testid="become-seller-submit" className="flex-1 py-2.5 rounded-full bg-[#d4af37] text-black font-semibold text-sm disabled:opacity-50">{busy ? "Attendere..." : "Conferma"}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function UserArea({ role, setRole, openLogin }) {
  const { user, logout } = useAuth();
  if (!user) {
    return (
      <>
        <select value={role === "seller" ? "b2c" : role} onChange={(e) => setRole(e.target.value)} data-testid="role-select"
          className="bg-[#090a0f] border border-white/10 rounded-full px-3 py-2.5 text-sm outline-none focus:border-[#d4af37]">
          <option value="b2c">Ospite: B2C</option>
          <option value="b2b">Ospite: B2B</option>
        </select>
        <button onClick={openLogin} data-testid="open-login-btn"
          className="flex items-center gap-2 rounded-full border border-[#d4af37]/50 px-4 py-2.5 text-sm hover:bg-[#d4af37] hover:text-[#0f1115] transition-colors">
          <LogIn className="w-4 h-4" /> Accedi / Registrati
        </button>
      </>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2 rounded-full border border-[#d4af37]/50 px-4 py-2 text-sm" data-testid="user-chip">
        <User className="w-4 h-4 text-[#d4af37]" />
        <span className="max-w-[140px] truncate" data-testid="active-role-label" title={user.is_admin ? user.nome_ragione_sociale : "Il tuo codice pubblico anonimo"}>{user.is_admin ? user.nome_ragione_sociale : canSellUser(user) ? user.seller_code : user.nome_ragione_sociale}</span>
        <span className="text-[10px] text-zinc-500" data-testid="user-profile-label">{profileLabel(user)}</span>
        {user.is_admin && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#d4af37] text-black" data-testid="admin-badge">ADMIN</span>}
      </div>
      {!canSellUser(user) && <BecomeSeller />}
      <button onClick={logout} data-testid="logout-btn" className="text-xs text-zinc-400 hover:text-white px-2">Esci</button>
    </div>
  );
}

function Header({ view, setView, search, setSearch, currency, setCurrency, role, setRole, mode, setMode, openLogin, favCount, onlyFavs, setOnlyFavs, bell }) {
  const { user } = useAuth();
  const [menu, setMenu] = useState(false);
  const mob = menu ? "flex" : "hidden";
  const go = (fn) => () => { fn(); setMenu(false); };
  return (
    <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/5 max-w-full">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-4 flex flex-wrap items-center gap-3 md:gap-4">
        <button onClick={go(() => setView("catalog"))} className="flex items-center gap-2 mr-auto md:mr-0" data-testid="logo-btn">
          <Gem className="w-7 h-7 text-[#d4af37]" />
          <span className="font-display text-3xl font-semibold tracking-tight">Kristall<span className="text-[#d4af37]">Hub</span></span>
        </button>
        <div className={`${menu ? "block" : "hidden"} md:block order-last md:order-none w-full md:w-auto md:flex-1 md:min-w-[200px] relative`}>
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setView("catalog"); }} placeholder="Cerca minerali, specie, miniere, venditori..."
            data-testid="search-input"
            className="w-full bg-[#090a0f] border border-white/10 focus:border-[#d4af37] outline-none rounded-full pl-11 pr-4 py-2.5 text-sm placeholder:text-zinc-500" />
        </div>
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="currency-select"
          className="hidden md:block bg-[#090a0f] border border-white/10 rounded-full px-3 py-2.5 text-sm outline-none focus:border-[#d4af37]">
          <option value="EUR">€ EUR</option>
          <option value="USD">$ USD</option>
        </select>
        {bell}
        <button onClick={() => setMenu(!menu)} className="md:hidden w-11 h-11 rounded-full border border-white/10 text-xl leading-none flex items-center justify-center" data-testid="mobile-menu-btn" aria-label="Menu">{menu ? "✕" : "☰"}</button>
        <div className={`${mob} md:flex order-last md:order-none w-full md:w-auto flex-wrap items-center gap-2`} data-testid="user-area-wrap">
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="currency-select-mobile"
            className="md:hidden bg-[#090a0f] border border-white/10 rounded-full px-3 py-2.5 text-sm outline-none">
            <option value="EUR">€ EUR</option>
            <option value="USD">$ USD</option>
          </select>
          <UserArea role={role} setRole={setRole} openLogin={openLogin} />
        </div>
      </div>
      <div className={`${mob} md:flex max-w-[1400px] mx-auto px-4 sm:px-8 pb-4 flex-wrap items-center justify-between gap-4`} data-testid="nav-wrap">
        <nav className="flex flex-col md:flex-row md:flex-wrap gap-2 w-full md:w-auto">
          <button onClick={go(() => { setView("catalog"); setOnlyFavs(false); })} data-testid="nav-catalog"
            className={`px-4 py-2 rounded-full text-sm transition-colors ${view === "catalog" && !onlyFavs ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"}`}>Esplora Catalogo</button>
          <button onClick={go(() => setView("auctions"))} data-testid="nav-auctions"
            className={`px-4 py-2 rounded-full text-sm flex items-center gap-1.5 transition-colors ${view === "auctions" ? "bg-orange-500/20 text-orange-400" : "text-zinc-400 hover:text-white"}`}>
            <Flame className="w-4 h-4 text-orange-500" /> Aste Live</button>
          <button onClick={go(() => { setView("catalog"); setOnlyFavs(!onlyFavs); })} data-testid="nav-favorites"
            className={`px-4 py-2 rounded-full text-sm flex items-center gap-1.5 transition-colors ${view === "catalog" && onlyFavs ? "bg-rose-500/20 text-rose-400" : "text-zinc-400 hover:text-white"}`}>
            <Heart className={`w-4 h-4 ${favCount ? "fill-rose-500 text-rose-500" : ""}`} /> Preferiti (<span data-testid="favorites-count">{favCount}</span>)</button>
          <button onClick={go(() => setView("sell"))} data-testid="nav-sell"
            className={`px-4 py-2 rounded-full text-sm flex items-center gap-1.5 transition-colors ${view === "sell" ? "bg-[#10b981]/20 text-[#10b981]" : "text-zinc-400 hover:text-white"}`}>
            <Sparkles className="w-4 h-4" /> Vendi con AI</button>
          {user && (
            <button onClick={go(() => setView("orders"))} data-testid="nav-orders"
              className={`px-4 py-2 rounded-full text-sm flex items-center gap-1.5 transition-colors ${view === "orders" ? "bg-[#d4af37]/20 text-[#d4af37]" : "text-zinc-400 hover:text-white"}`}>
              <Package className="w-4 h-4" /> {user.is_admin ? "Ordini Piattaforma" : "I miei Ordini"}</button>
          )}
        </nav>
        <ModeSwitch mode={mode} setMode={setMode} />
      </div>
    </header>
  );
}

function FilterRow({ label, items, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 w-20">{label}</span>
      {["Tutti", ...items].map((it) => (
        <button key={it} onClick={() => onChange(it)} data-testid={`${testid}-${it.toLowerCase().replace(/\s+/g, "-")}`}
          className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors ${value === it ? "bg-[#d4af37] text-[#0f1115] border-[#d4af37]" : "border-white/10 text-zinc-300 hover:border-[#d4af37]/60"}`}>
          {it}
        </button>
      ))}
    </div>
  );
}

function HeartBtn({ active, onToggle, id }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onToggle(id); }} data-testid={`fav-btn-${id}`} aria-label="Preferito"
      className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/70 backdrop-blur border border-white/10 flex items-center justify-center hover:scale-110 transition-transform">
      <Heart className={`w-4 h-4 transition-colors ${active ? "fill-rose-500 text-rose-500" : "text-white"}`} />
    </button>
  );
}

function CredBadge({ p }) {
  const c = p.venditore_credibilita ?? 100;
  const n = p.venditore_transazioni ?? 0;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] mb-4" data-testid={`cred-badge-${p.id}`}>
      <span title="Affidabilità del venditore: 100% per venditori con P.IVA o Collezionisti Privati con Codice Fiscale registrato; il numero accanto indica le transazioni completate con successo." className="px-2 py-0.5 rounded-full bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/40 font-semibold cursor-help">{c}% Credibilità Venditore{p.venditore_verificato !== false ? " · Verificato" : ""}</span>
      <span className="text-zinc-500">{n} transazioni completate</span>
      {p.venditore_privato && <span title="Venditore privato che cede pezzi della propria collezione, registrato con Codice Fiscale / Tax ID e senza Partita IVA." className="px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300 border border-sky-500/40 font-semibold cursor-help" data-testid={`private-badge-${p.id}`}>Collezionista Privato</span>}
    </div>
  );
}

function SellerProfileModal({ product, onClose }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!product.venditore_id) { setErr("Profilo non disponibile offline."); return; }
    apiFetch(`/sellers/${product.venditore_id}`).then(setS).catch((e) => setErr(e.message));
  }, [product.venditore_id]);
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="seller-profile-modal">
      <div className="bg-[#090a0f] border border-[#d4af37]/40 rounded-2xl p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Profilo Venditore</p>
            <h3 className="font-display text-3xl" data-testid="seller-profile-name">{s?.codice || sellerAlias(product)}</h3>
            <p className="text-xs text-zinc-500">Codice venditore anonimo{s?.nazione ? ` · ${s.nazione}` : ""}<Tip text="Per tutela della privacy e contro lo scavalcamento della piattaforma, il nome reale dei venditori non viene mai mostrato." /></p>
          </div>
          <button onClick={onClose} data-testid="seller-profile-close"><X className="w-5 h-5 text-zinc-400 hover:text-white" /></button>
        </div>
        {err && <p className="text-sm text-zinc-500">{err}</p>}
        {s && (
          <div className="grid grid-cols-3 gap-3 text-center">
            {[["Credibilità", `${s.credibilita}%`, "text-[#10b981]"], ["Transazioni", s.transazioni, "text-[#d4af37]"], ["Minerali", s.minerali, "text-white"]].map(([l, v, c]) => (
              <div key={l} className="bg-black border border-white/5 rounded-xl p-3"><div className={`text-2xl font-semibold ${c}`}>{v}</div><div className="text-[10px] text-zinc-500">{l}</div></div>
            ))}
            <p className="col-span-3 text-xs text-zinc-400 flex items-center justify-center gap-1 mt-2">
              <BadgeCheck className="w-4 h-4 text-[#10b981]" />{s.verificato ? "Venditore Verificato · P.IVA registrata" : "P.IVA non registrata"} · membro dal {new Date(s.created_at).toLocaleDateString("it-IT")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function OwnerActions({ p, onEdit, onDelete, className = "" }) {
  const { user } = useAuth();
  if (!user || !(user.is_admin || user.id === p.venditore_id)) return null;
  const stop = (fn) => (e) => { e.stopPropagation(); fn(p); };
  return (
    <div className={`flex gap-2 ${className}`}>
      <button onClick={stop(onEdit)} data-testid={`edit-btn-${p.id}`} className="px-3 py-1.5 rounded-full bg-[#d4af37] text-black text-[11px] font-semibold hover:brightness-110 transition-[filter]">✏️ Modifica</button>
      <button onClick={stop(onDelete)} data-testid={`admin-delete-${p.id}`} className="px-3 py-1.5 rounded-full bg-red-600/90 text-white text-[11px] font-semibold hover:bg-red-600 transition-colors">🗑️ Elimina{user.is_admin && user.id !== p.venditore_id ? " [Admin]" : ""}</button>
    </div>
  );
}

function EditMineralModal({ product, onClose, onSave }) {
  const keys = [["titolo", "Nome / Titolo *"], ["prezzo_b2c", "Prezzo (€) *"], ["img", "URL Foto *"], ["specie", "Specie"], ["origine", "Origine / Località"], ["nazione", "Nazione"],
    ["peso_g", "Peso (g)"], ["dimensioni_mm", "Dimensioni (mm)"], ["prezzo_b2b_kg", "Prezzo B2B €/Kg"], ["moq_kg", "MOQ Kg"], ["formula", "Formula IMA"], ["sistema_cristallino", "Sistema Cristallino"],
    ["spedizione_nazionale", "Spedizione Nazionale €"], ["spedizione_europa", "Spedizione Europa €"], ["spedizione_mondo", "Spedizione Mondo €"]];
  const [f, setF] = useState(() => Object.fromEntries([...keys.map(([k]) => [k, product[k] ?? ""]), ["descrizione", product.descrizione || ""]]));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (String(f.titolo).trim().length < 2 || !(Number(f.prezzo_b2c) > 0) || !String(f.img).trim()) { setErr("Nome, Prezzo (maggiore di 0) e Foto sono obbligatori."); return; }
    setBusy(true); setErr("");
    try { await onSave(product, f); } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md overflow-y-auto p-3 sm:p-8" onClick={onClose} data-testid="edit-mineral-modal">
      <div className="w-full max-w-3xl mx-auto bg-[#090a0f] border border-[#d4af37]/40 rounded-2xl p-5 sm:p-8 break-words" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5"><h3 className="font-display text-2xl">✏️ Modifica minerale</h3><button onClick={onClose} data-testid="edit-close-btn"><X className="w-5 h-5" /></button></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {keys.map(([k, l]) => (
            <label key={k} className="block"><span className="text-xs text-zinc-500">{l}</span>
              <input value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} className={inputCls} data-testid={`edit-field-${k}`} /></label>
          ))}
        </div>
        <label className="block mt-3"><span className="text-xs text-zinc-500">Descrizione</span>
          <textarea rows={4} value={f.descrizione} onChange={(e) => setF((x) => ({ ...x, descrizione: e.target.value }))} className={inputCls} data-testid="edit-field-descrizione" /></label>
        {err && <p className="mt-3 text-sm text-red-400" data-testid="edit-error">{err}</p>}
        <button onClick={save} disabled={busy} className="mt-5 w-full py-3 rounded-full bg-[#d4af37] text-black font-semibold disabled:opacity-50" data-testid="edit-save-btn">{busy ? "Salvataggio..." : "Salva Modifiche"}</button>
      </div>
    </div>
  );
}

function ProductCard({ p, mode, currency, onOpen, i, fav, toggleFav, onCert, onDelete, onEdit }) {
  const { user } = useAuth();
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(p)} data-testid={`product-card-${p.id}`} style={{ animationDelay: `${i * 60}ms` }}
      className="kh-reveal group cursor-pointer text-left bg-[#090a0f] rounded-2xl overflow-hidden border border-amber-500/20 hover:border-[#d4af37]/60 transition-[border-color,transform] duration-300 hover:-translate-y-1">
      <div className="relative aspect-[4/3] overflow-hidden">
        <Img src={p.img} alt={p.titolo} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <HeartBtn active={fav} onToggle={toggleFav} id={p.id} />
        {user && <OwnerActions p={p} onEdit={onEdit} onDelete={onDelete} className="absolute bottom-3 right-3 z-10" />}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {mode === "b2c" ? (
            <>
              <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-black/80 backdrop-blur text-[#d4af37] border border-[#d4af37]/40">Exact Piece (Pezzo Unico in foto)</span>
              <button onClick={(e) => { e.stopPropagation(); onCert(p); }} data-testid={`ai-verified-badge-${p.id}`} title="Scheda tecnica preliminare generata su parametri Mindat/IMA: clicca per vederla. Non è una certificazione gemmologica di laboratorio."
                className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#10b981]/90 text-[#0f1115] w-fit flex items-center gap-1 hover:bg-[#10b981] hover:shadow-[0_0_14px_#10b981] transition-shadow"><ShieldCheck className="w-3 h-3" />Scheda AI preliminare</button>
            </>
          ) : (
            <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#10b981]/90 text-[#0f1115]">Sconto volume fino a -15%</span>
          )}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1"><MapPin className="w-3 h-3" />{p.origine}</div>
        <h3 className="font-display text-xl leading-tight mb-2 group-hover:text-[#d4af37] transition-colors">{p.titolo}</h3>
        {formulaOf(p) && <span className="inline-block mb-2 px-2 py-0.5 rounded-md bg-white/5 border border-[#d4af37]/25 text-[11px] font-mono text-[#e9d58a] tracking-wide" title="Formula chimica ufficiale secondo l'International Mineralogical Association (IMA)" data-testid={`formula-${p.id}`}>{formulaOf(p)}</span>}
        <div className="flex items-center gap-1 text-xs text-zinc-400 mb-2">
          {sellerAlias(p)}{p.verificato && <BadgeCheck className="w-3.5 h-3.5 text-[#10b981]" />}
        </div>
        <CredBadge p={p} />
        {mode === "b2c" ? (
          <div className="flex items-end justify-between">
            <div className="text-xs text-zinc-500 space-y-0.5">
              <div>{p.peso_g} g</div><div>{p.dimensioni_mm} mm</div>
            </div>
            <div className="text-2xl font-semibold text-[#d4af37]" data-testid={`price-b2c-${p.id}`}>{fmt(p.prezzo_b2c, currency)}</div>
          </div>
        ) : (
          <div className="flex items-end justify-between">
            <div className="text-xs text-zinc-400">MOQ: <span className="text-white font-semibold">{p.moq_kg} Kg</span></div>
            <div className="text-right">
              <div className="text-2xl font-semibold text-[#10b981]" data-testid={`price-b2b-${p.id}`}>{fmt(p.prezzo_b2b_kg, currency)}<span className="text-sm text-zinc-400">/Kg</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const pad = (n) => String(n).padStart(2, "0");
function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function AuctionCard({ p, a, currency, onBid, onOpen, fav, toggleFav, now }) {
  const ms = Math.max(0, a.endsAt - now);
  const ended = ms === 0;
  const s = Math.floor(ms / 1000);
  return (
    <div className="bg-[#090a0f] rounded-2xl overflow-hidden border border-amber-500/20 hover:border-orange-500/50 transition-colors" data-testid={`auction-card-${p.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden cursor-pointer" onClick={() => onOpen(p)}>
        <Img src={p.img} alt={p.titolo} className="w-full h-full object-cover" />
        <HeartBtn active={fav} onToggle={toggleFav} id={p.id} />
        <span className="absolute top-3 left-3 flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full bg-orange-500 text-black">
          <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />LIVE
        </span>
      </div>
      <div className="p-5 space-y-4">
        <h3 className="font-display text-xl leading-tight">{p.titolo}</h3>
        <div className="flex items-center gap-2 bg-black border border-orange-500/30 rounded-xl px-4 py-3">
          <Timer className="w-4 h-4 text-orange-500" />
          <span className="text-xs text-zinc-400">{ended ? "Asta terminata" : "Scade tra"}</span>
          <span className="ml-auto font-mono text-lg text-orange-400 tabular-nums" data-testid={`auction-countdown-${p.id}`}>
            {pad(Math.floor(s / 3600))}h : {pad(Math.floor((s % 3600) / 60))}m : {pad(s % 60)}s
          </span>
        </div>
        <div className="flex justify-between items-end">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Offerta Attuale</div>
            <div className="text-2xl font-semibold text-[#d4af37]" data-testid={`auction-bid-${p.id}`}>{fmt(a.bid, currency)}</div>
          </div>
          <div className="text-right text-xs text-zinc-400 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5 text-[#10b981]" /><span data-testid={`auction-bids-count-${p.id}`}>{a.bids}</span> rilanci
          </div>
        </div>
        <button disabled={ended} onClick={() => onBid(p.id)} data-testid={`auction-bid-btn-${p.id}`}
          className="w-full py-3 rounded-full bg-gradient-to-r from-orange-500 to-[#d4af37] text-black font-semibold hover:shadow-[0_0_24px_rgba(249,115,22,0.5)] transition-shadow disabled:opacity-40">
          Rilancia Ora (+20€)
        </button>
      </div>
    </div>
  );
}

function Auctions({ products, auctions, setAuctions, currency, onOpen, favs, toggleFav }) {
  const now = useNow();
  const list = products.filter((p) => auctions[p.id]);
  const bid = (id) => setAuctions((as) => ({ ...as, [id]: { ...as[id], bid: as[id].bid + 20, bids: as[id].bids + 1 } }));
  return (
    <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12" data-testid="auctions-view">
      <p className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3 flex items-center gap-2"><Flame className="w-4 h-4" />Aste a Tempo · Live</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-12">Rilancia prima che <em className="text-[#d4af37]">scada il tempo.</em></h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map((p) => <AuctionCard key={p.id} p={p} a={auctions[p.id]} now={now} currency={currency} onBid={bid} onOpen={onOpen} fav={favs.includes(p.id)} toggleFav={toggleFav} />)}
      </div>
    </main>
  );
}

function Catalog({ products, mode, currency, search, onOpen, favs, toggleFav, onlyFavs, onCert, onDelete, onEdit }) {
  const [specie, setSpecie] = useState("Tutti");
  const [nazione, setNazione] = useState("Tutti");
  const [tipo, setTipo] = useState("Tutti");
  const [pMin, setPMin] = useState("");
  const [pMax, setPMax] = useState("");
  const priceOf = (p) => (mode === "b2c" ? p.prezzo_b2c : p.prezzo_b2b_kg);
  const species = [...new Set(products.map((p) => p.specie))];
  const nations = [...new Set(products.map((p) => p.nazione))];
  const q = search.trim().toLowerCase();
  const list = products.filter((p) =>
    (specie === "Tutti" || p.specie === specie) &&
    (nazione === "Tutti" || p.nazione === nazione) &&
    (pMin === "" || priceOf(p) >= Number(pMin)) &&
    (pMax === "" || priceOf(p) <= Number(pMax)) &&
    (tipo === "Tutti" || (tipo === "Collezionisti Privati") === !!p.venditore_privato) &&
    (!onlyFavs || favs.includes(p.id)) &&
    (!q || [p.titolo, p.specie, p.origine, sellerAlias(p)].join(" ").toLowerCase().includes(q)));
  return (
    <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12" data-testid="catalog-view">
      <div className="mb-12 max-w-3xl">
        <p className="text-xs uppercase tracking-[0.3em] text-[#10b981] mb-3">{mode === "b2c" ? "Collezione · Pezzi Unici" : "Ingrosso · Lotti al Kg"}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          {mode === "b2c" ? <>Minerali rari, <em className="text-[#d4af37]">esattamente</em> come in foto.</> : <>Forniture minerali <em className="text-[#10b981]">dirette</em> dalle miniere.</>}
        </h1>
        <p className="text-zinc-400 mt-4 text-sm sm:text-base">Ogni esemplare è verificato da Argon Vision AI e protetto da KristallHub Escrow.</p>
      </div>
      <div className="space-y-3 mb-10 bg-[#090a0f]/60 border border-white/5 rounded-2xl p-5">
        <p className="text-[11px] text-zinc-500">Usa i filtri per restringere il catalogo: tutti i filtri si combinano tra loro e con la ricerca in alto.</p>
        <FilterRow label="Specie" items={species} value={specie} onChange={setSpecie} testid="filter-specie" />
        <FilterRow label="Nazione" items={nations} value={nazione} onChange={setNazione} testid="filter-nazione" />
        <div className="flex items-center gap-3 pt-1">
          <span className="text-xs uppercase tracking-widest text-zinc-500 w-20">Venditore<Tip text="Venditori Professionisti: ditte con Partita IVA. Collezionisti Privati: persone fisiche che vendono pezzi della propria collezione." testid="tip-filter-venditore" /></span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="filter-venditore"
            className="bg-black border border-white/10 rounded-full px-4 py-1.5 text-sm outline-none focus:border-[#d4af37]">
            {["Tutti", "Venditori Professionisti", "Collezionisti Privati"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <span className="text-xs uppercase tracking-widest text-zinc-500 w-20">Prezzo<Tip text="Intervallo di prezzo in euro: inserisci un minimo e/o un massimo. In B2C vale il prezzo al pezzo, in B2B il prezzo al Kg." testid="tip-filter-prezzo" /></span>
          <input type="number" min="0" value={pMin} onChange={(e) => setPMin(e.target.value)} placeholder="Min €" data-testid="filter-price-min"
            className="w-24 bg-black border border-white/10 rounded-full px-4 py-1.5 text-sm outline-none focus:border-[#d4af37]" />
          <span className="text-zinc-600">–</span>
          <input type="number" min="0" value={pMax} onChange={(e) => setPMax(e.target.value)} placeholder="Max €" data-testid="filter-price-max"
            className="w-24 bg-black border border-white/10 rounded-full px-4 py-1.5 text-sm outline-none focus:border-[#d4af37]" />
          <span className="text-[11px] text-zinc-500">{mode === "b2c" ? "prezzo al pezzo (EUR)" : "prezzo al Kg (EUR)"}</span>
          {(pMin || pMax) && <button onClick={() => { setPMin(""); setPMax(""); }} className="text-xs text-[#d4af37] underline" data-testid="filter-price-reset">Azzera</button>}
        </div>
      </div>
      {list.length === 0 ? (
        <div className="text-center text-zinc-500 py-20" data-testid="catalog-empty">{onlyFavs ? "Nessun preferito salvato. Tocca il cuore su un minerale." : "Nessun minerale trovato."}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
          {list.map((p, i) => <ProductCard key={p.id} p={p} i={i} mode={mode} currency={currency} onOpen={onOpen} fav={favs.includes(p.id)} toggleFav={toggleFav} onCert={onCert} onDelete={onDelete} onEdit={onEdit} />)}
        </div>
      )}
    </main>
  );
}

function Chat({ product, chats, setChats, offers, setOffers, currency, role }) {
  const [text, setText] = useState("");
  const [offer, setOffer] = useState("");
  const [blocked, setBlocked] = useState(false);
  const { user, createOffer, openLogin } = useAuth();
  const [offerErr, setOfferErr] = useState("");
  const msgs = chats[product.id] || [];
  const myOffers = offers.filter((o) => o.productId === product.id && o.mine);
  const push = (m) => setChats((c) => ({ ...c, [product.id]: [...(c[product.id] || []), m] }));

  const sendMsg = () => {
    if (!text.trim()) return;
    if (isBlocked(text)) { setBlocked(true); return; }
    setBlocked(false);
    push({ from: "me", role, text, at: Date.now() });
    setText("");
    setTimeout(() => push({ from: "seller", text: "Grazie per il messaggio! Il pezzo è disponibile e spedito con tracciamento assicurato.", at: Date.now() }), 700);
  };
  const sendOffer = async () => {
    const v = Number(offer);
    if (!v || v <= 0) return;
    setBlocked(false); setOfferErr("");
    if (!user) { openLogin(); return; }
    try {
      await createOffer(product.id, v);
      push({ from: "me", role, text: `Proposta di prezzo: ${fmt(v, "EUR")}`, offer: true, at: Date.now() });
      setOffer("");
    } catch (e) { setOfferErr(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-black rounded-xl border border-white/10 p-4">
        <h4 className="flex items-center gap-2 font-semibold mb-3"><Gavel className="w-4 h-4 text-[#d4af37]" />Fai una Proposta di Prezzo</h4>
        <div className="flex gap-2">
          <input type="number" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Importo in €" data-testid="offer-input"
            className="flex-1 bg-[#090a0f] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#d4af37]" />
          <button onClick={sendOffer} data-testid="offer-submit-btn" className="px-4 py-2 rounded-lg bg-[#d4af37] text-[#0f1115] text-sm font-semibold hover:brightness-110 transition-[filter]">Invia Offerta</button>
        </div>
        {offerErr && <p className="mt-2 text-xs text-red-400" data-testid="offer-error">{offerErr}</p>}
        {!user && <p className="mt-2 text-[11px] text-zinc-500">Accedi per inviare un'offerta vincolante al venditore.</p>}
        {myOffers.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-zinc-400" data-testid="offers-list">
            {myOffers.map((o) => <li key={o.at}>· {fmt(o.amount, currency)} — {o.stato}</li>)}
          </ul>
        )}
      </div>
      <div className="bg-black rounded-xl border border-white/10 p-4">
        <h4 className="flex items-center gap-2 font-semibold mb-3"><Lock className="w-4 h-4 text-[#10b981]" />Chat Negoziazione · Protetta AI</h4>
        <div className="h-44 overflow-y-auto space-y-2 mb-3 pr-1" data-testid="chat-messages">
          {msgs.length === 0 && <p className="text-xs text-zinc-500">Scrivi al venditore {sellerAlias(product)}.</p>}
          {msgs.map((m, i) => (
            <div key={i} className={`max-w-[85%] text-sm px-3 py-2 rounded-xl ${m.from === "me" ? "ml-auto bg-[#d4af37]/15 text-[#f3e2a6]" : "bg-[#090a0f] text-zinc-300"} ${m.offer ? "border border-[#d4af37]/50" : ""}`}>{m.text}</div>
          ))}
        </div>
        {blocked && (
          <div className="mb-3 flex gap-2 items-start bg-red-600/15 border-2 border-red-500 text-red-300 rounded-lg p-3 text-sm font-semibold" role="alert" data-testid="chat-security-alert">
            <AlertTriangle className="w-5 h-5 shrink-0 text-red-500" />{BLOCK_MSG}
          </div>
        )}
        <div className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Scrivi un messaggio..." data-testid="chat-input"
            className="flex-1 bg-[#090a0f] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#10b981]" />
          <button onClick={sendMsg} data-testid="chat-send-btn" className="px-3 rounded-lg bg-[#10b981] text-[#0f1115] hover:brightness-110 transition-[filter]"><Send className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function Breakdown({ rows, total, totalLabel, testid }) {
  return (
    <div className="bg-black border border-white/10 rounded-xl p-4 text-sm space-y-2 text-left" data-testid={testid}>
      {rows.map(([l, v, cls]) => (
        <div key={l} className="flex justify-between gap-4"><span className="text-zinc-400">{l}</span><span className={cls || "text-zinc-200"}>{v}</span></div>
      ))}
      <div className="flex justify-between gap-4 border-t border-white/10 pt-2 font-semibold"><span>{totalLabel}</span><span className="text-[#d4af37]" data-testid={`${testid}-total`}>{total}</span></div>
    </div>
  );
}

function Choice({ options, value, onChange, testid }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {options.map(([k, l, sub]) => (
        <button type="button" key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`rounded-lg border px-2 py-2 text-xs transition-colors ${value === k ? "border-[#10b981] bg-[#10b981]/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
          <div className="font-semibold">{l}</div>{sub && <div className="text-[10px] text-zinc-500">{sub}</div>}
        </button>
      ))}
    </div>
  );
}

function Checkout({ product, mode, currency, onClose, amount, offerId }) {
  const { user, token, openLogin } = useAuth();
  const [fascia, setFascia] = useState("nazionale");
  const [metodo, setMetodo] = useState("bonifico");
  const [step, setStep] = useState(0);
  const [err, setErr] = useState("");
  const [order, setOrder] = useState(null);
  const prezzo = amount ?? (mode === "b2c" ? product.prezzo_b2c : product.prezzo_b2b_kg * product.moq_kg);
  const shipOf = (k) => Number(product[`spedizione_${k}`] ?? SHIP_DEFAULT[`spedizione_${k}`]);
  const sped = shipOf(fascia);
  const q = quote(prezzo, sped, metodo);
  const confirm = async () => {
    setStep(1); setErr("");
    try {
      const o = await apiFetch("/orders", { method: "POST", token, body: { mineral_id: product.id, spedizione_fascia: fascia, metodo_pagamento: metodo, modalita: mode, offer_id: offerId ?? null } });
      setOrder(o); setStep(2);
    } catch (e) { setErr(e.message); setStep(0); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" data-testid="checkout-modal">
      <div className="bg-[#090a0f] border border-[#10b981]/40 rounded-2xl p-6 sm:p-8 w-full max-w-md text-center my-8">
        {step === 0 && (<>
          <ShieldCheck className="w-10 h-10 text-[#10b981] mx-auto mb-3" />
          <h3 className="font-display text-3xl mb-1">Checkout Protetto Escrow</h3>
          <p className="text-sm text-zinc-400 mb-5">{product.titolo}{amount != null ? " · Prezzo negoziato" : mode === "b2b" && ` · ${product.moq_kg} Kg (MOQ)`}</p>
          <div className="space-y-4 text-left">
            <div><p className="text-xs text-zinc-500 mb-2">Fascia di spedizione (tracciata e assicurata)</p>
              <Choice value={fascia} onChange={setFascia} testid="ship-fascia" options={Object.entries(SHIP).map(([k, l]) => [k, l, fmt2(shipOf(k), currency)])} /></div>
            <div><p className="text-xs text-zinc-500 mb-2">Metodo di pagamento</p>
              <Choice value={metodo} onChange={setMetodo} testid="pay-method" options={[["bonifico", "Bonifico", "Fee 3%"], ["carta", "Carta / Stripe", "Fee 5%"]]} /></div>
            <Breakdown testid="checkout-breakdown" totalLabel="Totale stimato" total={fmt2(q.totale, currency)} rows={[
              ["Prezzo minerale", fmt2(prezzo, currency)],
              [`Spedizione ${SHIP[fascia]}`, `+ ${fmt2(sped, currency)}`],
              [`Fee Protezione Escrow (${metodo === "carta" ? "5%" : "3%"}, min 5 €)`, `+ ${fmt2(q.fee, currency)}`],
            ]} />
            <p className="text-[11px] text-zinc-500" data-testid="checkout-seller-net">Ricavo netto venditore: {fmt2(q.netto, currency)} (prezzo − commissione 5%, min 5 €: {fmt2(q.comm, currency)})</p>
            <p className="text-[11px] text-zinc-400 border border-white/10 rounded-lg p-2" data-testid="checkout-email-note">La transazione verrà gestita direttamente da KristallHub via email. Nessun pagamento online richiesto ora.</p>
          </div>
          {err && <p className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg p-3" data-testid="checkout-error">{err}</p>}
          <div className="flex gap-3 mt-6">
            <button onClick={onClose} className="flex-1 py-3 rounded-full border border-white/10 hover:border-white/30" data-testid="checkout-cancel-btn">Annulla</button>
            {user
              ? <button onClick={confirm} className="flex-1 py-3 rounded-full bg-[#10b981] text-[#0f1115] font-semibold" data-testid="checkout-confirm-btn">Invia Richiesta d'Ordine</button>
              : <button onClick={openLogin} className="flex-1 py-3 rounded-full bg-[#d4af37] text-[#0f1115] font-semibold" data-testid="checkout-login-btn">Accedi per acquistare</button>}
          </div>
        </>)}
        {step === 1 && (<div className="py-10"><div className="w-12 h-12 mx-auto mb-4 border-4 border-[#10b981]/30 border-t-[#10b981] rounded-full animate-spin" /><p className="text-zinc-300">Invio richiesta d'ordine...</p></div>)}
        {step === 2 && order && (<>
          <CheckCircle2 className="w-14 h-14 text-[#10b981] mx-auto mb-4" />
          <h3 className="font-display text-3xl mb-1" data-testid="checkout-success">Richiesta Inviata</h3>
          <p className="font-mono text-[#d4af37] mb-1" data-testid="checkout-order-id">{order.id}</p>
          <p className="text-xs text-zinc-500 mb-4">Stato: richiesta ricevuta</p>
          <div className="text-3xl font-semibold text-[#d4af37] mb-4" data-testid="checkout-total">{fmt2(order.totale_acquirente, currency)}</div>
          <p className="text-sm text-zinc-400 mb-6">
            KristallHub ti contatterà via email da info@kristallhub.com per confermare disponibilità, spedizione e modalità di pagamento. Nessun pagamento online richiesto ora.
          </p>
          <button onClick={onClose} className="w-full py-3 rounded-full bg-[#d4af37] text-[#0f1115] font-semibold" data-testid="checkout-close-btn">Chiudi</button>
        </>)}
      </div>
    </div>
  );
}

function DetailModal({ product, mode, currency, onClose, role, chats, setChats, offers, setOffers, onCert, onEdit, onDelete }) {
  const [checkout, setCheckout] = useState(false);
  const [showSeller, setShowSeller] = useState(false);
  const specs = [
    [Gem, "Specie", product.specie], [MapPin, "Origine", product.origine], [Scale, "Peso", `${product.peso_g} g`],
    [Ruler, "Dimensioni", `${product.dimensioni_mm} mm`], [ShieldCheck, "AI Score", product.ai_score], [Package, "MOQ B2B", `${product.moq_kg} Kg`],
    ...(mineralOf(product) ? [[FlaskConical, "Formula IMA", mineralOf(product)[1]], [Hexagon, "Sistema Cristallino", mineralOf(product)[2]]] : []),
  ];
  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-md overflow-y-auto overflow-x-hidden p-0 sm:p-8" onClick={onClose} data-testid="product-detail-modal">
      <div className="w-full max-w-full md:max-w-6xl mx-auto bg-[#090a0f] border border-white/10 sm:rounded-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="grid grid-cols-1 md:grid-cols-2 break-words whitespace-normal">
          <div className="min-w-0"><MediaGallery product={product} onClose={onClose} /></div>
          <div className="w-full max-w-full min-w-0 px-4 py-6 sm:p-10 space-y-6">
            <OwnerActions p={product} onEdit={onEdit} onDelete={onDelete} />
            <div className="flex justify-between items-start gap-4">
              <div>
                <button onClick={() => setShowSeller(true)} data-testid="detail-seller-btn" className="text-xs text-zinc-500 hover:text-[#d4af37] flex items-center gap-1 mb-1 transition-colors">{product.venditore_codice}{product.verificato && <span className="flex items-center gap-1 text-[#10b981]"><BadgeCheck className="w-3.5 h-3.5" />Verificato</span>}<span className="underline ml-1">Vedi profilo</span></button>
                <CredBadge p={product} />
                <h2 className="font-display text-3xl sm:text-4xl leading-tight" data-testid="detail-title">{product.titolo}</h2>
              </div>
              <button onClick={onClose} className="hidden lg:block text-zinc-400 hover:text-white" data-testid="detail-close-btn-desktop"><X className="w-6 h-6" /></button>
            </div>
            <div className="flex items-baseline gap-4">
              {mode === "b2c"
                ? <span className="text-4xl font-semibold text-[#d4af37]" data-testid="detail-price">{fmt(product.prezzo_b2c, currency)}</span>
                : <span className="text-4xl font-semibold text-[#10b981]" data-testid="detail-price">{fmt(product.prezzo_b2b_kg, currency)}<span className="text-base text-zinc-400">/Kg · MOQ {product.moq_kg} Kg</span></span>}
            </div>
            {mode === "b2b" && (
              <div className="grid grid-cols-3 gap-2 text-center text-xs" data-testid="volume-discounts">
                {[["≥ MOQ", "0%"], [`≥ ${product.moq_kg * 3} Kg`, "-8%"], [`≥ ${product.moq_kg * 10} Kg`, "-15%"]].map(([a, b]) => (
                  <div key={a} className="bg-black rounded-lg p-2 border border-white/5"><div className="text-zinc-500">{a}</div><div className="text-[#10b981] font-semibold">{b}</div></div>
                ))}
              </div>
            )}
            <p className="text-sm text-zinc-300 leading-relaxed">{product.descrizione}</p>
            <div className="grid grid-cols-2 gap-3">
              {specs.map(([I, l, v]) => (
                <div key={l} className="bg-black rounded-xl p-3 border border-white/5">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500"><I className="w-3 h-3" />{l}</div>
                  <div className="text-sm mt-1">{v}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-3 items-start bg-[#10b981]/10 border border-[#10b981]/40 rounded-xl p-4" data-testid="escrow-badge">
              <Lock className="w-5 h-5 text-[#10b981] shrink-0" />
              <p className="text-sm"><span className="font-semibold text-[#10b981]">KristallHub Escrow</span> — Pagamento congelato fino alla consegna verificata.</p>
            </div>
            <button onClick={() => onCert(product)} data-testid="detail-certificate-btn"
              className="w-full py-3 rounded-full border border-[#d4af37]/60 text-[#d4af37] flex items-center justify-center gap-2 hover:bg-[#d4af37]/10 transition-colors">
              <ShieldCheck className="w-4 h-4" /> Scheda AI preliminare · Vedi Scheda
            </button>
            <p className="text-[11px] text-zinc-500 -mb-1" data-testid="buy-now-help">"Acquista Ora" invia una richiesta d'ordine: scegli spedizione e metodo, vedi il totale stimato e KristallHub gestirà la trattativa via email. Nessun pagamento online ora. Puoi anche fare una proposta di prezzo qui sotto.</p>
            <button onClick={() => setCheckout(true)} data-testid="buy-now-btn" title="Invia una richiesta d'ordine: nessun pagamento online richiesto ora"
              className="w-full py-4 rounded-full bg-[#d4af37] text-[#0f1115] font-semibold flex items-center justify-center gap-2 hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-shadow">
              <ShoppingCart className="w-5 h-5" /> Acquista Ora
            </button>
            <Chat product={product} chats={chats} setChats={setChats} offers={offers} setOffers={setOffers} currency={currency} role={role} />
          </div>
        </div>
      </div>
      {checkout && <div onClick={(e) => e.stopPropagation()}><Checkout product={product} mode={mode} currency={currency} onClose={() => setCheckout(false)} /></div>}
      {showSeller && <div onClick={(e) => e.stopPropagation()}><SellerProfileModal product={product} onClose={() => setShowSeller(false)} /></div>}
    </div>
  );
}

const EMPTY_FORM = { titolo: "", specie: "", origine: "", nazione: "", purezza: "", peso_g: "", dimensioni_mm: "", prezzo_b2c: "", prezzo_b2b_kg: "", moq_kg: "", img: "", descrizione: "", tr_it: "", tr_en: "", tr_zh: "", video: "", categoria: "", proprieta: "", certificazione: "Nessuna", durezza: "", lucentezza: "", formula: "", sistema_cristallino: "", spedizione_nazionale: "15", spedizione_europa: "35", spedizione_mondo: "70" };
const AI_RESULT_NOTE = "Rilevamento client-side: nome file + analisi RGB/HSL dei pixel centrali";
const SPECIES = {
  ametista: { label: "Ametista", swatch: "#8b5cf6", titolo: "Ametista Imperiale su Matrice", specie: "Quarzo var. Ametista", origine: "Uruguay - Artigas", nazione: "Uruguay", peso_g: 1800, dimensioni_mm: "160 x 120 x 90", prezzo_b2c: 390, prezzo_b2b_kg: 80, moq_kg: 20, descrizione: "Quarzo var. ametista in drusa su matrice basaltica, viola profondo da Fe³⁺ e irraggiamento naturale. Durezza 7 Mohs, sistema trigonale, lucentezza vitrea.", tr_it: "Ametista imperiale naturale su matrice, pezzo unico.", tr_en: "Natural imperial amethyst on matrix, one-of-a-kind specimen.", tr_zh: "天然帝王紫水晶原石基座，独一无二的标本。" },
  smeraldo: { label: "Smeraldo", swatch: "#059669", titolo: "Smeraldo Grezzo su Matrice", specie: "Berillo var. Smeraldo", origine: "Colombia - Muzo", nazione: "Colombia", peso_g: 340, dimensioni_mm: "85 x 60 x 42", prezzo_b2c: 780, prezzo_b2b_kg: 520, moq_kg: 3, descrizione: "Berillo var. smeraldo prismatico esagonale con inclusioni 'jardin' tipiche di Muzo, cromoforo Cr³⁺/V³⁺. IR 1.57-1.58, durezza 7.5-8 Mohs.", tr_it: "Smeraldo grezzo colombiano su matrice, qualità collezione.", tr_en: "Rough Colombian emerald on matrix, collector grade.", tr_zh: "哥伦比亚祖母绿原石基座，收藏级。" },
  fluorite: { label: "Fluorite Verde", swatch: "#4ade80", titolo: "Fluorite Verde Ottaedrica", specie: "Fluorite", origine: "Pakistan - Gilgit", nazione: "Pakistan", peso_g: 900, dimensioni_mm: "70 x 65 x 60", prezzo_b2c: 110, prezzo_b2b_kg: 38, moq_kg: 40, descrizione: "CaF₂ cubico in ottaedri a sfaldatura perfetta, verde menta trasparente con fluorescenza UV. Durezza 4 Mohs.", tr_it: "Fluorite verde naturale ottaedrica.", tr_en: "Natural green octahedral fluorite.", tr_zh: "天然绿色八面体萤石。" },
  azzurrite: { label: "Azzurrite", swatch: "#1d4ed8", titolo: "Azzurrite Cristallizzata", specie: "Azzurrite", origine: "Marocco - Touissit", nazione: "Marocco", peso_g: 420, dimensioni_mm: "90 x 70 x 45", prezzo_b2c: 320, prezzo_b2b_kg: 140, moq_kg: 10, descrizione: "Carbonato basico di rame monoclino, cristalli tabulari blu notte a lucentezza vitreo-adamantina. Durezza 3.5-4 Mohs.", tr_it: "Azzurrite cristallizzata da collezione.", tr_en: "Collector-grade crystallized azurite.", tr_zh: "收藏级蓝铜矿晶体。" },
  lapislazzuli: { label: "Lapislazzuli", swatch: "#1e3a8a", titolo: "Lapislazzuli Royal Blue", specie: "Lapislazzuli (roccia a Lazurite)", origine: "Afghanistan - Badakhshan", nazione: "Afghanistan", peso_g: 1500, dimensioni_mm: "140 x 95 x 70", prezzo_b2c: 190, prezzo_b2b_kg: 65, moq_kg: 25, descrizione: "Roccia metamorfica a lazurite con inclusioni di pirite dorata e calcite, blu reale uniforme. Durezza 5-5.5 Mohs.", tr_it: "Lapislazzuli afgano blu reale.", tr_en: "Royal blue Afghan lapis lazuli.", tr_zh: "阿富汗皇家蓝青金石。" },
  citrino: { label: "Quarzo Citrino", swatch: "#f59e0b", titolo: "Quarzo Citrino Naturale", specie: "Quarzo var. Citrino", origine: "Brasile - Rio Grande do Sul", nazione: "Brasile", peso_g: 600, dimensioni_mm: "110 x 70 x 55", prezzo_b2c: 240, prezzo_b2b_kg: 70, moq_kg: 15, descrizione: "Quarzo var. citrino giallo-ambra naturale (non trattato termicamente), punte trigonali limpide. Durezza 7 Mohs.", tr_it: "Citrino naturale brasiliano non trattato.", tr_en: "Untreated natural Brazilian citrine.", tr_zh: "未经处理的天然巴西黄水晶。" },
  pirite: { label: "Pirite Cubica", swatch: "#ca8a04", titolo: "Pirite Cubica Specchiata", specie: "Pirite", origine: "Perù - Huanzala", nazione: "Perù", peso_g: 750, dimensioni_mm: "80 x 75 x 60", prezzo_b2c: 160, prezzo_b2b_kg: 45, moq_kg: 20, descrizione: "FeS₂ in cubi geminati a lucentezza metallica oro, facce specchiate con striature. Durezza 6-6.5 Mohs.", tr_it: "Pirite cubica peruviana a specchio.", tr_en: "Mirror-faced cubic Peruvian pyrite.", tr_zh: "秘鲁镜面立方黄铁矿。" },
  rodocrosite: { label: "Rodocrosite", swatch: "#f472b6", titolo: "Rodocrosite Rosa Bandata", specie: "Rodocrosite", origine: "Argentina - Capillitas", nazione: "Argentina", peso_g: 380, dimensioni_mm: "90 x 60 x 35", prezzo_b2c: 290, prezzo_b2b_kg: 120, moq_kg: 8, descrizione: "MnCO₃ trigonale rosa lampone con bandature concentriche stalattitiche. Durezza 3.5-4 Mohs.", tr_it: "Rodocrosite argentina bandata.", tr_en: "Banded Argentine rhodochrosite.", tr_zh: "阿根廷条纹菱锰矿。" },
  rubellite: { label: "Tormalina Rubellite", swatch: "#be123c", titolo: "Tormalina Rubellite su Quarzo", specie: "Elbaite var. Rubellite", origine: "Brasile - Minas Gerais", nazione: "Brasile", peso_g: 210, dimensioni_mm: "70 x 40 x 30", prezzo_b2c: 860, prezzo_b2b_kg: 640, moq_kg: 2, descrizione: "Elbaite var. rubellite rosso-magenta, prismi striati terminati su quarzo. Pleocroismo marcato, durezza 7-7.5 Mohs.", tr_it: "Rubellite brasiliana su quarzo.", tr_en: "Brazilian rubellite on quartz.", tr_zh: "巴西红碧玺共生石英。" },
  tormalina: { label: "Tormalina", swatch: "#14b8a6", titolo: "Tormalina su Quarzo", specie: "Elbaite (Supergruppo Tormalina)", origine: "Brasile - Minas Gerais", nazione: "Brasile", peso_g: 180, dimensioni_mm: "65 x 40 x 32", prezzo_b2c: 1100, prezzo_b2b_kg: 900, moq_kg: 2, descrizione: "Elbaite in prismi trigonali striati su quarzo ialino, colorazione cuprifera. Durezza 7-7.5 Mohs.", tr_it: "Tormalina brasiliana su quarzo.", tr_en: "Brazilian tourmaline on quartz.", tr_zh: "巴西碧玺共生石英。" },
  malachite: { label: "Malachite", swatch: "#15803d", titolo: "Malachite Botrioidale", specie: "Malachite", origine: "Congo - Katanga", nazione: "Congo", peso_g: 820, dimensioni_mm: "100 x 80 x 50", prezzo_b2c: 180, prezzo_b2b_kg: 55, moq_kg: 20, descrizione: "Carbonato di rame botrioidale a bande verdi concentriche, superficie sericea. Durezza 3.5-4 Mohs.", tr_it: "Malachite botrioidale del Katanga.", tr_en: "Botryoidal malachite from Katanga.", tr_zh: "加丹加葡萄状孔雀石。" },
  quarzo_ialino: { label: "Quarzo Ialino", swatch: "#e5e7eb", titolo: "Quarzo Ialino Alpino", specie: "Quarzo var. Cristallo di Rocca", origine: "Alpi Svizzere", nazione: "Svizzera", peso_g: 450, dimensioni_mm: "120 x 60 x 50", prezzo_b2c: 210, prezzo_b2b_kg: 60, moq_kg: 10, descrizione: "SiO₂ trigonale cristallo di rocca limpido, terminazioni integre da fessura alpina. Durezza 7 Mohs.", tr_it: "Cristallo di rocca alpino limpido.", tr_en: "Water-clear Alpine rock crystal.", tr_zh: "阿尔卑斯透明水晶。" },
  quarzo_fume: { label: "Quarzo Fumé Gwindel", swatch: "#57534e", titolo: "Quarzo Fumé Gwindel Alpino", specie: "Quarzo var. Fumé (Gwindel)", origine: "Alpi Svizzere", nazione: "Svizzera", peso_g: 260, dimensioni_mm: "78 x 52 x 30", prezzo_b2c: 280, prezzo_b2b_kg: 110, moq_kg: 5, descrizione: "Quarzo fumé a crescita elicoidale gwindel, colore da irraggiamento naturale di Al. Durezza 7 Mohs.", tr_it: "Gwindel di quarzo fumé svizzero.", tr_en: "Swiss smoky quartz gwindel.", tr_zh: "瑞士烟晶扭曲晶。" },
};
const NAME_KEYS = [
  ["quarzo_fume", ["fume", "smoky", "gwindel"]], ["citrino", ["citrino", "citrine"]], ["ametista", ["ametista", "amethyst"]],
  ["smeraldo", ["smeraldo", "emerald", "berillo", "beryl"]], ["rubellite", ["rubellite"]], ["tormalina", ["tormalina", "tourmaline", "paraiba"]],
  ["azzurrite", ["azzurrite", "azurite"]], ["fluorite", ["fluorite", "fluorit"]], ["pirite", ["pirite", "pyrite"]],
  ["lapislazzuli", ["lapis"]], ["malachite", ["malachite"]], ["rodocrosite", ["rodocrosite", "rhodochrosite"]],
  ["quarzo_ialino", ["quarzo", "quartz", "ialino", "rock crystal"]],
];
const matchName = (name) => {
  const n = (name || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const hit = NAME_KEYS.find(([, words]) => words.some((w) => n.includes(w)));
  return hit ? hit[0] : null;
};
const rgbToHsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2, d = max - min;
  if (!d) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h = (h * 60 + 360) % 360;
  return { h, s, l };
};
const analyzePixels = (src) => new Promise((resolve, reject) => {
  const im = new Image();
  if (!src.startsWith("data:")) im.crossOrigin = "anonymous";
  im.onerror = reject;
  im.onload = () => {
    try {
      const c = document.createElement("canvas");
      c.width = 120; c.height = 120;
      const ctx = c.getContext("2d");
      ctx.drawImage(im, 0, 0, 120, 120);
      const { data } = ctx.getImageData(30, 30, 60, 60);
      const all = [0, 0, 0, 0], sat = [0, 0, 0, 0];
      for (let i = 0; i < data.length; i += 4) {
        const [r, g, b] = [data[i], data[i + 1], data[i + 2]];
        all[0] += r; all[1] += g; all[2] += b; all[3]++;
        const p = rgbToHsl(r, g, b);
        if (p.s > 0.25 && p.l > 0.1 && p.l < 0.9) { sat[0] += r; sat[1] += g; sat[2] += b; sat[3]++; }
      }
      const pool = sat[3] > all[3] * 0.15 ? sat : all;
      const [r, g, b] = [0, 1, 2].map((k) => Math.round(pool[k] / pool[3]));
      resolve({ r, g, b, ...rgbToHsl(r, g, b) });
    } catch (e) { reject(e); }
  };
  im.src = src;
});
const byColor = ({ h, s, l }) => {
  if (s < 0.18 || l < 0.1 || l > 0.92) return l > 0.5 ? "quarzo_ialino" : "quarzo_fume";
  if (h >= 255 && h < 330) return "ametista";
  if (h >= 330 || h < 15) return l > 0.6 ? "rodocrosite" : "rubellite";
  if (h < 70) return s < 0.4 ? "pirite" : "citrino";
  if (h < 170) return l > 0.5 ? "fluorite" : "smeraldo";
  return h >= 215 && l < 0.4 ? "lapislazzuli" : "azzurrite";
};
const detectSpecies = async (src, name) => {
  const k = matchName(name);
  if (k) return { key: k, method: "name", name };
  try {
    const color = await analyzePixels(src);
    return { key: byColor(color), method: "color", color };
  } catch {
    return { key: "quarzo_ialino", method: "fallback" };
  }
};
const CATEGORIA = {
  ametista: "Silicati · Tectosilicati (Gruppo del Quarzo)", quarzo_ialino: "Silicati · Tectosilicati (Gruppo del Quarzo)", quarzo_fume: "Silicati · Tectosilicati (Gruppo del Quarzo)",
  citrino: "Silicati · Tectosilicati (Gruppo del Quarzo)", smeraldo: "Silicati · Ciclosilicati (Gruppo del Berillo)", tormalina: "Silicati · Ciclosilicati (Supergruppo Tormalina)",
  rubellite: "Silicati · Ciclosilicati (Supergruppo Tormalina)", fluorite: "Alogenuri", azzurrite: "Carbonati", malachite: "Carbonati", rodocrosite: "Carbonati (Gruppo della Calcite)",
  pirite: "Solfuri (Gruppo della Pirite)", lapislazzuli: "Roccia metamorfica (Lazurite · Feldspatoidi)",
};
const buildForm = (key) => {
  const s = SPECIES[key];
  return {
    titolo: s.titolo, specie: s.specie, origine: s.origine, nazione: s.nazione, purezza: "99%", peso_g: String(s.peso_g), dimensioni_mm: s.dimensioni_mm,
    prezzo_b2c: String(s.prezzo_b2c), prezzo_b2b_kg: String(s.prezzo_b2b_kg), moq_kg: String(s.moq_kg), descrizione: s.descrizione, tr_it: s.tr_it, tr_en: s.tr_en, tr_zh: s.tr_zh,
    categoria: CATEGORIA[key] || "Minerale", proprieta: (s.descrizione.match(/Durezza[^.]*|IR[^,]*/g) || []).join(" · "),
  };
};

const HINT_WARN = "Scrivi il nome del minerale, gemma o fossile nella casella qui sopra (es. Ammonite, Sfera di Ametista Rosa) e clicca '⚡ Compila Scheda Tecnica AI' per generare la scheda tecnica.";
const fileHint = (name) => {
  const t = (name || "").replace(/\.[a-z0-9]{2,5}$/i, "").replace(/[_\-.()[\]]+/g, " ").replace(/\d+/g, " ").replace(/\s+/g, " ").trim();
  if (t.length < 3 || /^(img|image|dsc|dscn|pxl|photo|foto|screenshot|schermata|whatsapp|wa|picture|pic|immagine|scan|camera|cam|mvimg|edit|copy|file|download|unnamed|ig|insta|snap)\b/i.test(t)) return "";
  return t;
};
const SELL_HELP = {
  titolo: "Nome con cui il minerale apparirà nel catalogo (es. Drusa di Ametista, Sfera di Quarzo Rosa).",
  specie: "Specie mineralogica ufficiale (es. Quarzo var. Ametista). Se la lasci vuota useremo il Nome.",
  categoria: "Classe mineralogica (es. Silicati, Carbonati, Fossili).",
  origine: "Luogo o miniera di estrazione (es. Minas Gerais, Brasile / Madagascar).",
  nazione: "Paese di estrazione (es. Brasile).",
  purezza: "Stima della naturalezza o purezza (es. Naturale, 99%).",
  peso_g: "Peso dell'esemplare in grammi (g) o carati (ct). Lascia vuoto se non lo conosci.",
  dimensioni_mm: "Misure in millimetri (es. 80 x 60 x 40).",
  prezzo_b2c: "Prezzo di vendita richiesto (inserisci solo il numero, es. 45).",
  prezzo_b2b_kg: "Prezzo all'ingrosso al Kg per i grossisti B2B (solo il numero).",
  moq_kg: "Ordine minimo in Kg per i grossisti (es. 5).",
  img: "Incolla il link di una foto se non la carichi dal dispositivo. Serve almeno una foto.",
  formula: "Composizione chimica ufficiale secondo l'International Mineralogical Association (es. SiO₂ per il quarzo).",
  sistema_cristallino: "Struttura geometrica dei cristalli (es. Trigonale, Cubico, Monoclino, Esagonale, Amorfo).",
  durezza: "Resistenza alla scalfittura da 1 (tenero, es. Talco) a 10 (durissimo, es. Diamante).",
  lucentezza: "Modo in cui la superficie riflette la luce (es. Vitrea, Metallica, Perlacea, Setosa, Resinosa).",
  certificazione: "Indica l'eventuale certificato di laboratorio (es. GIA, IGI) oppure Nessuna.",
  spedizione_nazionale: "Costo di spedizione tracciata nel tuo paese (solo il numero, es. 15).",
  spedizione_europa: "Costo di spedizione tracciata verso l'Europa (es. 35).",
  spedizione_mondo: "Costo di spedizione tracciata verso il resto del mondo (es. 70).",
};
const SELL_PLACEHOLDER = { titolo: "es. Sfera di Ametista Rosa", prezzo_b2c: "es. 45", peso_g: "es. 350", origine: "es. Minas Gerais, Brasile", formula: "es. SiO₂", sistema_cristallino: "es. Trigonale", durezza: "es. 7", lucentezza: "es. Vitrea", img: "https://...", dimensioni_mm: "es. 80 x 60 x 40" };
const pickSuggestion = (typed, nome) => {
  const words = typed.trim().split(/\s+/);
  const first = nome.toLowerCase().split(/\s+/)[0];
  const i = words.findIndex((w) => first.startsWith(w.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")));
  return [...(i >= 0 ? words.slice(0, i) : words.slice(0, -1)), nome].join(" ");
};

function IdentifyPanel({ r }) {
  const rows = [["Specie (IMA)", r.specie], ["Forma", r.forma], ["Categoria", r.categoria], ["Formula", r.formula], ["Sistema", r.sistema_cristallino], ["Durezza Mohs", r.durezza], ["Lucentezza", r.lucentezza]];
  return (
    <div className="mt-4 bg-black border border-[#10b981]/40 rounded-xl p-4" data-testid="identify-panel">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{r.appreso ? "Riconosciuto dal database appreso dai venditori" : "Riconosciuto dal database scientifico interno"}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        {rows.map(([k, v]) => <div key={k} className="min-w-0"><span className="text-zinc-500">{k}: </span><span className="text-zinc-200" data-testid={`identify-${k.split(" ")[0].toLowerCase()}`}>{v}</span></div>)}
      </div>
    </div>
  );
}

function DetectionPanel({ detection, onChange }) {
  const s = SPECIES[detection.key];
  const c = detection.color;
  return (
    <div className="mt-4 bg-black border border-[#10b981]/40 rounded-xl p-4 space-y-3" data-testid="detection-panel">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-lg border border-white/20" style={{ background: c ? `rgb(${c.r},${c.g},${c.b})` : s.swatch }} data-testid="detection-swatch" />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Specie rilevata</div>
          <div className="font-semibold text-[#10b981]" data-testid="detection-species">{s.label}</div>
        </div>
      </div>
      <p className="text-[11px] text-zinc-400" data-testid="detection-method">
        {detection.manual ? "Specie affinata manualmente dal venditore."
          : detection.method === "name" ? `Rilevata dal nome file: "${detection.name}"`
            : detection.method === "color" ? `Colore medio RGB(${c.r}, ${c.g}, ${c.b}) · Hue ${Math.round(c.h)}° · Sat ${Math.round(c.s * 100)}% · Luce ${Math.round(c.l * 100)}%`
              : "Analisi pixel non disponibile: specie neutra predefinita."}
      </p>
      <label className="block">
        <span className="text-xs text-zinc-500">Cambia Specie Rilevata</span>
        <select value={detection.key} onChange={(e) => onChange(e.target.value)} data-testid="change-species-select"
          className="mt-1 w-full bg-[#090a0f] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#10b981]">
          {Object.entries(SPECIES).map(([k, v]) => <option key={k} value={k}>{v.label} · {v.origine}</option>)}
        </select>
      </label>
    </div>
  );
}

const certIdOf = (p) => {
  let h = 7;
  for (const ch of `${p.id}${p.titolo}`) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return `KH-ARG-${h.toString(36).toUpperCase().padStart(7, "0").slice(0, 7)}`;
};

function CertificateModal({ product, onClose }) {
  const id = certIdOf(product);
  const rows = [["Specie", product.specie], ["Origine", product.origine], ["Peso", `${product.peso_g} g`], ["Dimensioni", `${product.dimensioni_mm} mm`], ["Venditore", sellerAlias(product)], ["Esito AI", product.ai_score], ["Certificazione Lab", product.certificazione || "Nessuna"]];
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="certificate-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl">
        <div id="kh-cert" className="relative rounded-3xl p-8 sm:p-10 bg-gradient-to-br from-[#0b0b0b] via-black to-[#14110a] border-2 border-[#d4af37] shadow-[0_0_60px_rgba(212,175,55,0.25)] overflow-hidden">
          <div className="absolute inset-3 rounded-2xl border border-[#d4af37]/30 pointer-events-none" />
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-[#d4af37]"><Gem className="w-5 h-5" /><span className="font-display text-xl">KristallHub</span></div>
              <h3 className="font-display text-3xl sm:text-4xl mt-3 text-[#f3e2a6]">Scheda AI preliminare (Argon Vision)</h3>
              <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mt-2">AI Argon Vision · Gemmological Report</p>
            </div>
            <div className="bg-white p-2 rounded-lg shrink-0" data-testid="certificate-qr">
              <QRCodeSVG value={`${window.location.origin}/?cert=${id}&p=${encodeURIComponent(product.id)}`} size={96} fgColor="#000000" bgColor="#ffffff" />
            </div>
          </div>
          <div className="mt-8 grid sm:grid-cols-[140px_1fr] gap-6">
            <Img src={product.img} alt={product.titolo} className="w-36 h-36 rounded-xl object-cover border border-[#d4af37]/40" />
            <div>
              <div className="font-display text-2xl">{product.titolo}</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-3 text-sm">
                {rows.map(([k, v]) => <div key={k}><div className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</div><div className="text-zinc-200">{v}</div></div>)}
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap items-end justify-between gap-4 border-t border-[#d4af37]/30 pt-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">ID Certificato</div>
              <div className="font-mono text-lg text-[#d4af37]" data-testid="certificate-id">{id}</div>
            </div>
            <div className="flex items-center gap-2 text-[#10b981] text-sm font-semibold"><ShieldCheck className="w-5 h-5" />99% Naturale · Pezzo Unico</div>
          </div>
        </div>
        <div className="kh-noprint flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-white/15 hover:border-white/40" data-testid="certificate-close-btn">Chiudi</button>
          <button onClick={() => window.print()} className="flex-1 py-3 rounded-full bg-[#d4af37] text-black font-semibold flex items-center justify-center gap-2" data-testid="certificate-print-btn">
            <Printer className="w-4 h-4" /> Stampa / Scarica Certificato
          </button>
        </div>
      </div>
    </div>
  );
}

const isMine = (o) => o.mine;
const OFFER_LABEL = { in_attesa: "In attesa", accettata: "Accettata · In attesa pagamento Escrow", rifiutata: "Rifiutata", pagata: "Ordinata · Pagamento in Escrow" };
const fromOffer = (o, uid) => ({
  ...o, at: o.id, productId: o.mineral_id, raw: o.stato, unread: o.unread_buyer, mine: o.buyer_id === uid,
  cliente: `${o.buyer_nome} (${o.buyer_ruolo === "Acquirente_B2B" ? "B2B" : SELLER_RUOLI.includes(o.buyer_ruolo) ? "Venditore" : "B2C"})`,
  stato: o.stato === "controproposta" ? `Controproposta: ${fmt(o.counter, "EUR")}` : OFFER_LABEL[o.stato] || o.stato,
});

function NotificationBell({ offers, products, currency, onPay, onRead, shipments = [], seenShip = [], onSeenShip, adminPending = 0, onReview, notes = [], onReadNotes }) {
  const [open, setOpen] = useState(false);
  const list = offers.filter((o) => isMine(o) && o.raw !== "in_attesa" && o.raw !== "pagata").reverse();
  const newShip = shipments.filter((s) => !seenShip.includes(s.id));
  const unreadNotes = notes.filter((n) => !n.read).length;
  const unread = list.filter((o) => o.unread).length + newShip.length + adminPending + unreadNotes;
  const toggle = () => {
    if (!open && unreadNotes) onReadNotes();
    if (!open && list.some((o) => o.unread)) onRead();
    if (!open && newShip.length) onSeenShip(shipments.map((s) => s.id));
    setOpen(!open);
  };
  return (
    <div className="relative">
      <button onClick={toggle} data-testid="notifications-bell" className="relative w-11 h-11 rounded-full border border-white/10 flex items-center justify-center hover:border-[#d4af37] transition-colors">
        <Bell className={`w-5 h-5 ${unread ? "text-[#d4af37]" : "text-zinc-300"}`} />
        {unread > 0 && <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-rose-500 text-[10px] font-bold flex items-center justify-center animate-pulse" data-testid="notifications-count">{unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-[#090a0f] border border-amber-500/30 rounded-2xl shadow-2xl p-3 z-50" data-testid="notifications-panel">
          <div className="text-xs uppercase tracking-widest text-zinc-500 px-2 py-1">Esito delle tue offerte</div>
          {notes.slice(0, 8).map((n) => (
            <div key={n.id} className={`p-2 rounded-xl text-sm ${n.read ? "" : "bg-[#d4af37]/5"}`} data-testid={`notification-note-${n.id}`}>
              <div className="font-medium text-[#d4af37]">{n.subject}</div><div className="text-xs text-zinc-400 break-words">{n.msg}</div>
            </div>
          ))}
          {adminPending > 0 && (
            <button onClick={() => { setOpen(false); onReview(); }} data-testid="notification-learned-pending"
              className="w-full flex gap-3 p-2 rounded-xl hover:bg-white/5 text-left">
              <FlaskConical className="w-10 h-10 p-2 rounded-lg bg-amber-500/10 text-amber-300 shrink-0" />
              <div className="text-sm"><div className="font-medium text-amber-300">{adminPending} minerali in attesa di revisione</div><div className="text-xs text-zinc-400 underline">Vai alla Revisione Database Appreso</div></div>
            </button>
          )}
          {shipments.map((s) => (
            <div key={s.id} className="flex gap-3 p-2 rounded-xl hover:bg-white/5" data-testid={`notification-ship-${s.id}`}>
              <Package className="w-10 h-10 p-2 rounded-lg bg-sky-500/10 text-sky-400 shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <div className="truncate font-medium">{s.mineral?.titolo}</div>
                <div className="text-sky-400">Spedito con {s.tracking.corriere}</div>
                <a href={`https://t.17track.net/it#nums=${encodeURIComponent(s.tracking.tracking_number)}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs font-mono underline text-zinc-300 hover:text-white" data-testid={`notification-track-${s.id}`}>{s.tracking.tracking_number}</a>
              </div>
            </div>
          ))}
          {list.length === 0 && shipments.length === 0 && <p className="text-sm text-zinc-500 px-2 py-4">Nessuna risposta dai venditori per ora.</p>}
          {list.map((o) => {
            const p = products.find((x) => x.id === o.productId);
            const acc = o.stato.startsWith("Accettata"), cnt = o.stato.startsWith("Controproposta");
            const pay = acc ? o.amount : o.counter;
            return (
              <div key={o.at} className="flex gap-3 p-2 rounded-xl hover:bg-white/5" data-testid={`notification-${o.at}`}>
                <Img src={p?.img} alt={p?.titolo} className="w-12 h-12 rounded-lg object-cover" />
                <div className="flex-1 min-w-0 text-sm">
                  <div className="truncate font-medium">{p?.titolo}</div>
                  <div className={acc ? "text-[#10b981]" : cnt ? "text-sky-400" : "text-red-400"}>
                    {acc ? `Offerta di ${fmt(o.amount, currency)} accettata` : cnt ? `Controproposta: ${fmt(o.counter, currency)}` : `Offerta di ${fmt(o.amount, currency)} rifiutata`}
                  </div>
                  {(acc || cnt) && p && pay > 0 && (
                    <button onClick={() => { setOpen(false); onPay(p, pay, o.id); }} data-testid={`notification-pay-${o.at}`}
                      className="mt-1.5 text-xs px-3 py-1.5 rounded-full bg-[#10b981] text-black font-semibold flex items-center gap-1">
                      <Lock className="w-3 h-3" />{acc ? "Paga ora in Escrow" : `Accetta e paga ${fmt(pay, currency)}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OfferRow({ o, product, currency, update }) {
  const [counter, setCounter] = useState("");
  const tone = o.stato.startsWith("Accettata") ? "text-[#10b981] border-[#10b981]/40" : o.stato === "Rifiutata" ? "text-red-400 border-red-500/40" : o.stato.startsWith("Controproposta") ? "text-sky-400 border-sky-500/40" : "text-[#d4af37] border-[#d4af37]/40";
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-4 bg-black border border-amber-500/20 rounded-xl p-4" data-testid={`offer-row-${o.at}`}>
      <Img src={product?.img} alt={product?.titolo} className="w-16 h-16 rounded-lg object-cover" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{product?.titolo || "Minerale"}</div>
        <div className="text-xs text-zinc-500">{o.cliente || "Cliente"} · listino {fmt(product?.prezzo_b2c, currency)}</div>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-lg font-semibold text-[#d4af37]">{fmt(o.amount, currency)}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${tone}`} data-testid={`offer-status-${o.at}`}>{o.stato}</span>
        </div>
      </div>
      {(o.raw === "in_attesa" || o.raw === "controproposta") && (
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => update(o.id, "accetta")} data-testid={`offer-accept-${o.at}`}
          className="px-3 py-2 rounded-lg bg-[#10b981] text-black text-xs font-semibold hover:brightness-110 transition-[filter]">Accetta Offerta</button>
        <button onClick={() => update(o.id, "rifiuta")} data-testid={`offer-reject-${o.at}`}
          className="px-3 py-2 rounded-lg border border-red-500/50 text-red-400 text-xs hover:bg-red-500/10 transition-colors">Rifiuta</button>
        <input type="number" value={counter} onChange={(e) => setCounter(e.target.value)} placeholder="€" data-testid={`offer-counter-input-${o.at}`}
          className="w-20 bg-[#090a0f] border border-white/10 rounded-lg px-2 py-2 text-xs outline-none focus:border-sky-500" />
        <button onClick={() => { if (Number(counter) > 0) { update(o.id, "controproposta", Number(counter)); setCounter(""); } }}
          data-testid={`offer-counter-btn-${o.at}`}
          className="px-3 py-2 rounded-lg border border-sky-500/50 text-sky-400 text-xs hover:bg-sky-500/10 transition-colors">Invia Controproposta (€)</button>
      </div>
      )}
    </div>
  );
}

function OffersPanel({ offers, products, currency }) {
  const { actOffer } = useAuth();
  const list = offers.filter((o) => !o.mine);
  return (
    <section className="mt-12 bg-[#090a0f] border border-amber-500/20 rounded-2xl p-6" data-testid="offers-panel">
      <h2 className="font-display text-3xl mb-6 flex items-center gap-3"><Inbox className="w-6 h-6 text-[#d4af37]" />Offerte Ricevute dai Clienti <span className="text-base text-zinc-500">({list.length})</span></h2>
      {list.length === 0 && <p className="text-sm text-zinc-500">Nessuna offerta ricevuta.</p>}
      <div className="space-y-3">
        {[...list].reverse().map((o) => <OfferRow key={o.at} o={o} product={products.find((p) => p.id === o.productId)} currency={currency} update={actOffer} />)}
      </div>
    </section>
  );
}

const CSV_HEAD = "Titolo,Specie,Miniera,Nazione,Purezza,Peso_g,Dimensioni_mm,Prezzo_B2C,Prezzo_B2B_Kg,MOQ_Kg,Foto_URL,Video_URL,spedizione_nazionale,spedizione_europa,spedizione_mondo,Categoria,Formula_IMA,Sistema_Cristallino,Durezza,Lucentezza";
const CSV_SAMPLE = `${CSV_HEAD}
Drusa Ametista Viola Profondo,Ametista,Uruguay - Artigas,Uruguay,99%,2400,"200 x 150 x 120",420,82,20,https://images.unsplash.com/photo-1627768919962-fb6af351759c?w=800,,15,35,70,Silicati · Tectosilicati (Gruppo del Quarzo),SiO₂,Trigonale,7,Vitrea
Pirite Cubica Specchiata,Pirite,Perù - Huanzala,Perù,98%,750,"80 x 75 x 60",160,45,20,https://images.unsplash.com/photo-1562162135-9f64f33e623b?w=800,https://www.youtube.com/watch?v=dQw4w9WgXcQ,12,30,65,Solfuri (Gruppo della Pirite),FeS₂,Cubico (Isometrico),6-6.5,Metallica
`;
const parseCsvLine = (line) => {
  const out = [];
  let cur = "", q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
    else if ((ch === "," || ch === ";") && !q) { out.push(cur.trim()); cur = ""; }
    else cur += ch;
  }
  out.push(cur.trim());
  return out;
};

const LEARN_FIELDS = [["nome", "Nome"], ["categoria", "Categoria"], ["formula", "Formula IMA"], ["sistema", "Sistema"], ["durezza", "Durezza"], ["lucentezza", "Lucentezza"], ["origine", "Origine"]];

function LearnedRow({ d, onSave, onDelete }) {
  const [f, setF] = useState(d);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");
  useEffect(() => { setF(d); }, [d]);
  const pending = d.stato !== "approvato";
  const submit = async (stato, label) => {
    setSaving(true); setSaved("");
    const ok = await onSave(d.id, { ...f, stato });
    setSaving(false);
    if (ok) { setSaved(label); setTimeout(() => setSaved(""), 3000); }
  };
  return (
    <div className={`bg-black border rounded-xl p-4 ${pending ? "border-amber-500/40" : "border-[#10b981]/30"}`} data-testid={`learned-row-${d.id}`}>
      <div className="flex justify-between items-center mb-3">
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${pending ? "border-amber-500/50 text-amber-300" : "border-[#10b981]/50 text-[#10b981]"}`} data-testid={`learned-status-${d.id}`}>{pending ? "In revisione · non visibile ai venditori" : "Approvato · attivo nei suggerimenti"}</span>
        <span className="text-[10px] text-zinc-500">{new Date(d.created_at).toLocaleDateString("it-IT")}</span>
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        {LEARN_FIELDS.map(([k, l]) => (
          <label key={k} className="block"><span className="text-[10px] text-zinc-500">{l}</span>
            <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`learned-${k}-${d.id}`}
              className="w-full bg-[#090a0f] border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#d4af37]" /></label>
        ))}
      </div>
      <textarea rows={2} value={f.descrizione || ""} onChange={(e) => setF({ ...f, descrizione: e.target.value })} data-testid={`learned-descrizione-${d.id}`}
        className="mt-2 w-full bg-[#090a0f] border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#d4af37]" />
      <div className="flex flex-wrap gap-2 mt-3">
        <button type="button" disabled={saving} onClick={() => submit("approvato", pending ? "Voce approvata" : "Correzioni salvate")} data-testid={`learned-approve-${d.id}`}
          className="px-3 py-1.5 rounded-lg bg-[#10b981] text-black text-xs font-semibold disabled:opacity-50">{saving ? "Salvataggio..." : pending ? "Approva" : "Salva Correzioni"}</button>
        {!pending && <button type="button" disabled={saving} onClick={() => submit("in_revisione", "Voce sospesa")} data-testid={`learned-suspend-${d.id}`}
          className="px-3 py-1.5 rounded-lg border border-amber-500/50 text-amber-300 text-xs disabled:opacity-50">Sospendi</button>}
        <button onClick={() => window.confirm(`Eliminare "${d.nome}" dal database appreso?`) && onDelete(d.id)} data-testid={`learned-delete-${d.id}`}
          className="px-3 py-1.5 rounded-lg border border-red-500/50 text-red-400 text-xs">Elimina</button>
        {saved && <span className="self-center text-xs text-[#10b981] flex items-center gap-1" data-testid={`learned-saved-${d.id}`}><CheckCircle2 className="w-3.5 h-3.5" />{saved}</span>}
      </div>
    </div>
  );
}

function LearnedReview() {
  const { token } = useAuth();
  const [list, setList] = useState([]);
  const [err, setErr] = useState("");
  useEffect(() => { apiFetch("/admin/learned", { token }).then(setList).catch((e) => setErr(e.message)); }, [token]);
  const save = async (id, body) => {
    const { nome, categoria, formula, sistema, durezza, lucentezza, origine, descrizione, stato } = body;
    const clean = Object.fromEntries(Object.entries({ nome, categoria, formula, sistema, durezza, lucentezza, origine, descrizione, stato }).map(([k, v]) => [k, v == null ? "" : String(v)]));
    try { const u = await apiFetch(`/admin/learned/${id}`, { method: "PATCH", token, body: clean }); setList((l) => l.map((x) => (x.id === id ? u : x))); return true; } catch (e) { window.alert(e.message); return false; }
  };
  const del = async (id) => {
    try { await apiFetch(`/admin/learned/${id}`, { method: "DELETE", token }); setList((l) => l.filter((x) => x.id !== id)); } catch (e) { window.alert(e.message); }
  };
  const pending = list.filter((d) => d.stato !== "approvato").length;
  return (
    <section className="mt-12 bg-[#090a0f] border border-amber-500/20 rounded-2xl p-6" data-testid="learned-review-panel">
      {pending > 0 && (
        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/50 text-amber-300 text-xs font-semibold" data-testid="learned-pending-badge">
          <Bell className="w-4 h-4 animate-pulse" />{pending} nuovi minerali in attesa di revisione
        </div>
      )}
      <h2 className="font-display text-3xl mb-2 flex items-center gap-3"><FlaskConical className="w-6 h-6 text-[#d4af37]" />Revisione Database Appreso <span className="text-base text-zinc-500">({pending} da approvare)</span></h2>
      <p className="text-xs text-zinc-500 mb-6">I minerali appresi dalle pubblicazioni dei venditori entrano nei suggerimenti e nella compilazione automatica solo dopo la tua approvazione.</p>
      {err && <p className="text-sm text-red-400">{err}</p>}
      {list.length === 0 && !err && <p className="text-sm text-zinc-500">Nessun minerale appreso finora.</p>}
      <div className="space-y-3">{[...list].sort((a, b) => (a.stato === "approvato") - (b.stato === "approvato")).map((d) => <LearnedRow key={d.id} d={d} onSave={save} onDelete={del} />)}</div>
    </section>
  );
}

function CsvImport({ onPublish }) {
  const [msg, setMsg] = useState("");
  const download = () => {
    const url = URL.createObjectURL(new Blob(["\uFEFF" + CSV_SAMPLE], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = "template_kristalhub.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const lines = String(reader.result).replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim());
      const rows = lines.slice(1).map(parseCsvLine).filter((c) => c[0]);
      const results = await Promise.allSettled(rows.map((c, i) => {
        const [titolo, specie, origine, nazione, purezza, peso, dim, b2c, b2b, moq, foto, video, sn, se, sm, categoria, formula, sistema, durezza, lucentezza] = c;
        return onPublish({
          spedizione_nazionale: sn || SHIP_DEFAULT.spedizione_nazionale, spedizione_europa: se || SHIP_DEFAULT.spedizione_europa,
          spedizione_mondo: sm || SHIP_DEFAULT.spedizione_mondo, purezza: purezza || "99%",
          id: `kh-csv-${Date.now()}-${i}`, titolo, specie: specie || "Minerale", origine: origine || "", nazione: nazione || (origine || "").split(" - ")[0],
          venditore_codice: "Seller #KH-NEW", verificato: true, peso_g: Number(peso) || 0, dimensioni_mm: dim || "",
          prezzo_b2c: Number(b2c) || 0, prezzo_b2b_kg: Number(b2b) || 0, moq_kg: Number(moq) || 1,
          img: foto || FALLBACK_IMG, images: [], video: video || "", ai_score: `${purezza || "99%"} Naturale - Scheda AI preliminare`, pezzo_unico: true,
          descrizione: `${titolo} – ${specie} da ${origine}. Importato da catalogo CSV.`,
          categoria: categoria || "", formula: formula || "", sistema_cristallino: sistema || "", durezza: durezza || "", lucentezza: lucentezza || "",
          proprieta: [formula && `Formula IMA: ${formula}`, sistema && `Sistema: ${sistema}`, durezza && `Durezza Mohs: ${durezza}`, lucentezza && `Lucentezza: ${lucentezza}`].filter(Boolean).join(" · "),
        });
      }));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const ko = results.find((r) => r.status === "rejected");
      setMsg(`${ok} minerali caricati con successo nel catalogo!${ko ? ` (${rows.length - ok} righe scartate: ${ko.reason?.message})` : ""}`);
    };
    reader.readAsText(file);
  };
  return (
    <div className="mb-6" data-testid="csv-import-box">
      <div className="bg-[#0b0c10] border border-[#D4AF37]/40 rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-center justify-between">
        <h3 className="font-semibold text-[#d4af37]">📦 Importazione Rapida Catalogo (CSV)</h3>
        <div className="flex flex-wrap gap-2">
          <button onClick={download} data-testid="csv-template-btn"
            className="px-4 py-2 rounded-lg border border-white/15 text-sm hover:border-[#d4af37] transition-colors">Scarica Template CSV</button>
          <label className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#d4af37] to-[#10b981] text-black text-sm font-semibold cursor-pointer hover:brightness-110 transition-[filter]" data-testid="csv-upload-btn">
            Carica Catalogo CSV
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="csv-file-input" />
          </label>
        </div>
      </div>
      {msg && <p className="-mt-3 text-sm text-[#10b981] flex items-center gap-2" data-testid="csv-success-msg"><CheckCircle2 className="w-4 h-4" />{msg}</p>}
    </div>
  );
}

function SellDashboard({ onPublish, onDelete, onEdit, openLogin, offers, setOffers, products, currency }) {
  const { user } = useAuth();
  const canSell = canSellUser(user);
  const [pubErr, setPubErr] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [scanning, setScanning] = useState(false);
  const [done, setDone] = useState(false);
  const [photos, setPhotos] = usePersist("kh_sell_photos", [null, null, null, null]);
  const [photoName, setPhotoName] = useState("");
  const [detection, setDetection] = useState(null);
  const [aiHint, setAiHint] = useState("");
  const [scanWarn, setScanWarn] = useState("");
  const [nameList, setNameList] = useState([]);
  const [showSug, setShowSug] = useState(false);
  useEffect(() => { apiFetch("/ai/names").then(setNameList).catch(() => {}); }, [done]);
  const suggestions = useMemo(() => {
    const words = aiHint.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").split(/\s+/).filter((w) => w.length >= 2);
    const last = words[words.length - 1];
    if (!last) return [];
    const norm = (t) => t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return nameList.filter((s) => norm(s.nome).split(/\s+/).some((w) => w.startsWith(last)) || norm(s.nome).startsWith(words.slice(-2).join(" "))).slice(0, 8);
  }, [aiHint, nameList]);
  const safePhotos = Array.isArray(photos) ? photos : [null, null, null, null];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [fErr, setFErr] = useState({});
  const scan = async (nameArg) => {
    const hint = aiHint.trim() || fileHint(typeof nameArg === "string" ? nameArg : photoName);
    setScanWarn(""); setShowSug(false);
    if (hint.length < 2) { setScanWarn(HINT_WARN); setDetection(null); return; }
    const main = safePhotos.find(Boolean) || "";
    setScanning(true);
    try {
      const [r] = await Promise.all([apiFetch("/ai/identify", { method: "POST", body: { nome: hint } }), new Promise((ok) => setTimeout(ok, 1500))]);
      if (!r.riconosciuto) { setScanWarn(aiHint.trim() ? r.messaggio : HINT_WARN); setDetection(null); return; }
      setDetection(r);
      const fields = ["titolo", "specie", "categoria", "origine", "nazione", "purezza", "descrizione", "proprieta", "formula", "sistema_cristallino", "durezza", "lucentezza", "dimensioni_mm", "tr_it", "tr_en", "tr_zh"];
      setForm((f) => ({
        ...f, ...Object.fromEntries(fields.map((k) => [k, r[k] ?? ""])),
        peso_g: String(r.peso_g), prezzo_b2c: String(r.prezzo_b2c), prezzo_b2b_kg: String(r.prezzo_b2b_kg), moq_kg: String(r.moq_kg),
        img: f.img || (main.startsWith("data:") ? "" : main),
      }));
      setFErr({});
    } catch (e) { setScanWarn(e.message); } finally { setScanning(false); }
  };
  const publish = async () => {
    const e = {};
    if (form.titolo.trim().length < 2) e.titolo = "Inserisci il nome del minerale (almeno 2 caratteri).";
    if (!(Number(String(form.prezzo_b2c).replace(",", ".")) > 0)) e.prezzo_b2c = "Inserisci un prezzo valido maggiore di 0 (solo il numero, es. 45).";
    if (!safePhotos.some(Boolean) && !form.img.trim()) e.img = "Carica almeno una foto dal dispositivo o incolla l'URL di una foto.";
    setFErr(e);
    if (Object.keys(e).length) { setPubErr("Compila i campi obbligatori evidenziati in rosso: Nome, Prezzo e Foto."); return; }
    setPublishing(true); setPubErr("");
    try {
    await onPublish({
      purezza: form.purezza || "99%", spedizione_nazionale: form.spedizione_nazionale, spedizione_europa: form.spedizione_europa, spedizione_mondo: form.spedizione_mondo,
      id: `kh-${Date.now()}`, titolo: form.titolo.trim(), specie: form.specie.trim() || form.titolo.trim(), origine: form.origine, nazione: form.nazione || form.origine.split(" - ")[0],
      venditore_codice: user?.seller_code || "Seller #KH-NEW", verificato: true, peso_g: Number(form.peso_g) || 0, dimensioni_mm: form.dimensioni_mm,
      prezzo_b2c: Number(String(form.prezzo_b2c).replace(",", ".")) || 0, prezzo_b2b_kg: Number(form.prezzo_b2b_kg) || 0, moq_kg: Number(form.moq_kg) || 1,
      img: safePhotos.find(Boolean) || form.img.trim(), images: safePhotos.filter(Boolean), video: form.video || "", ai_score: `${form.purezza || "99%"} Naturale - Scheda AI preliminare`, pezzo_unico: true,
      descrizione: form.descrizione, traduzioni: { it: form.tr_it, en: form.tr_en, zh: form.tr_zh },
      categoria: form.categoria, proprieta: form.proprieta, certificazione: form.certificazione || "Nessuna", formula: form.formula, sistema_cristallino: form.sistema_cristallino, durezza: form.durezza, lucentezza: form.lucentezza,
    });
    setPhotos([null, null, null, null]);
    setDone(true);
    } catch (e) { setPubErr(e.message); }
    setPublishing(false);
  };

  if (!canSell) {
    return (
      <main className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="sell-locked">
        <Lock className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
        <h2 className="font-display text-4xl mb-3">Area Venditori</h2>
        <p className="text-zinc-400 mb-6">{user ? "Il tuo profilo è da Acquirente: abilita il profilo venditore con il Codice Fiscale (privato) o la Partita IVA (professionista). Continuerai a poter acquistare normalmente." : "Per pubblicare minerali devi accedere con il tuo account."}</p>
        {user ? <BecomeSeller big /> : <button onClick={openLogin} className="px-6 py-3 rounded-full bg-[#d4af37] text-[#0f1115] font-semibold" data-testid="sell-login-btn">Accedi</button>}
      </main>
    );
  }
  const fields = [
    ["titolo", "Nome / Titolo *"], ["prezzo_b2c", "Prezzo (€) *"], ["img", "URL Foto * (se non carichi foto dal dispositivo)"], ["specie", "Specie Mindat/IMA (Facoltativo)"],
    ["categoria", "Categoria (Facoltativo)"], ["origine", "Origine / Località (Facoltativo)"], ["nazione", "Nazione (Facoltativo)"], ["peso_g", "Peso (Facoltativo)"],
    ["dimensioni_mm", "Dimensioni mm (Facoltativo)"], ["purezza", "Purezza stimata (Facoltativo)"], ["formula", "Formula Chimica IMA (Facoltativo)"], ["sistema_cristallino", "Sistema Cristallino (Facoltativo)"],
    ["durezza", "Durezza Mohs (Facoltativo)"], ["lucentezza", "Lucentezza (Facoltativo)"], ["prezzo_b2b_kg", "Prezzo B2B €/Kg (Facoltativo)"], ["moq_kg", "MOQ Kg (Facoltativo)"],
    ["certificazione", "Certificazione Gemmologica Ufficiale (Facoltativo)"],
    ["spedizione_nazionale", "Spedizione Nazionale € (Facoltativo)"], ["spedizione_europa", "Spedizione Europa € (Facoltativo)"], ["spedizione_mondo", "Spedizione Resto del Mondo € (Facoltativo)"],
  ];
  return (
    <main className="max-w-[1200px] mx-auto px-4 sm:px-8 py-12" data-testid="sell-view">
      <p className="text-xs uppercase tracking-[0.3em] text-[#10b981] mb-3">Dashboard Venditore</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-10">Vendi in 5 Secondi <em className="text-[#d4af37]">con AI</em></h1>
      <CsvImport onPublish={onPublish} />
      <div className="grid lg:grid-cols-[380px_1fr] gap-8">
        <div className="bg-[#090a0f] border border-white/5 rounded-2xl p-6 h-fit">
          <div className="relative aspect-square rounded-xl overflow-hidden border border-dashed border-[#d4af37]/40 mb-6 bg-black">
            {(safePhotos.find(Boolean) || form.img) ? <Img src={safePhotos.find(Boolean) || form.img} alt="anteprima" className="w-full h-full object-cover" testid="sell-preview-img" />
              : <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500"><Upload className="w-10 h-10 mb-2" /><span className="text-sm">Foto del minerale</span></div>}
            {scanning && (
              <div className="absolute inset-0 bg-[#10b981]/10" data-testid="scan-overlay">
                <div className="kh-scanline absolute left-0 right-0 h-1 bg-[#10b981] shadow-[0_0_20px_#10b981]" />
              </div>
            )}
          </div>
          <PhotoUploader photos={safePhotos} setPhotos={setPhotos} onFirstPhoto={(img, name) => { setPhotoName(name); setFErr((x) => ({ ...x, img: "" })); if (aiHint.trim() || fileHint(name)) scan(name); else setScanWarn(HINT_WARN); }} />
          {fErr.img && <p className="-mt-2 mb-3 text-[11px] text-red-400 border border-red-500 rounded-lg p-2" data-testid="sell-error-photo">{fErr.img}</p>}
          <label className="block mb-3 relative">
            <span className="text-xs text-zinc-400">Nome / Indizio del minerale, gemma o fossile<Tip text="Il sistema riconosce il nome (o il nome del file foto, es. ammonite.webp) e compila i dati scientifici." /></span>
            <input value={aiHint} onChange={(e) => { setAiHint(e.target.value); setShowSug(true); }} onFocus={() => setShowSug(true)} onBlur={() => setShowSug(false)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scan(); } }}
              placeholder="es. Ammonite, Sfera Ametista Rosa..." data-testid="ai-hint-input" autoComplete="off"
              className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#10b981]" />
            <Help>Scrivi il nome del minerale, gemma o fossile (es. Ammonite, Sfera Ametista Rosa) e clicca su '⚡ Compila Scheda Tecnica AI' per riempire automaticamente i dati scientifici.</Help>
            {showSug && suggestions.length > 0 && (
              <ul className="absolute z-30 left-0 right-0 bottom-full mb-1 max-h-60 overflow-y-auto bg-[#090a0f] border border-[#10b981]/40 rounded-lg shadow-2xl" data-testid="ai-hint-suggestions">
                {suggestions.map((s) => (
                  <li key={s.nome}>
                    <button type="button" onMouseDown={(e) => { e.preventDefault(); setAiHint(pickSuggestion(aiHint, s.nome)); setShowSug(false); }}
                      data-testid={`ai-hint-sug-${s.nome.replace(/\s+/g, "-").toLowerCase()}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#10b981]/10 flex justify-between gap-3">
                      <span className="text-zinc-100">{s.nome}</span><span className="text-[11px] text-zinc-500 truncate">{s.specie}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </label>
          <button type="button" onMouseDown={() => setShowSug(false)} onClick={() => scan()} disabled={scanning} data-testid="ai-scan-btn"
            className="w-full py-4 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#10b981] text-[#0f1115] font-semibold flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(16,185,129,0.35)] hover:brightness-110 transition-[filter] disabled:opacity-70">
            ⚡ Compila Scheda Tecnica AI
          </button>
          {scanning && <p className="text-sm text-[#10b981] mt-4 text-center animate-pulse" data-testid="scan-status">Compilazione della scheda tecnica dal database scientifico in corso...</p>}
          {!scanning && scanWarn && <p className="mt-4 text-sm text-red-300 border border-red-500/50 bg-red-500/10 rounded-lg p-3" data-testid="scan-warning">{scanWarn}</p>}
          {!scanning && detection && <IdentifyPanel r={detection} />}
          {!scanning && detection && (
            <p className="mt-3 text-[11px] text-amber-300/90 border border-amber-500/30 bg-amber-500/5 rounded-lg p-2" data-testid="ai-legal-note">
              Scheda tecnica preliminare generata con supporto AI su parametri Mindat/IMA. Non costituisce certificazione gemmologica di laboratorio.
            </p>
          )}
          {!scanning && detection && <p className="mt-2 text-[11px] text-zinc-500" data-testid="ai-hint-used">Tutti i campi restano modificabili a mano prima della pubblicazione.</p>}
          <p className="text-[10px] text-zinc-600 mt-3 text-center">Dati da database scientifico interno (Mindat/IMA) e minerali appresi dai venditori · nessun costo AI</p>
        </div>
        <div className="bg-[#090a0f] border border-white/5 rounded-2xl p-6">
          {done ? (
            <div className="text-center py-16" data-testid="publish-success">
              <CheckCircle2 className="w-14 h-14 text-[#10b981] mx-auto mb-4" />
              <h3 className="font-display text-3xl mb-2">Pubblicato sul Marketplace!</h3>
              <p className="text-zinc-400 mb-6">Il tuo minerale è ora visibile nel Catalogo.</p>
              <button onClick={() => { setForm(EMPTY_FORM); setDetection(null); setPhotoName(""); setDone(false); }} className="px-6 py-3 rounded-full border border-[#d4af37] text-[#d4af37]" data-testid="publish-another-btn">Carica un altro</button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                {fields.map(([k, l]) => (
                  <label key={k} className="block">
                    <span className="text-xs text-zinc-500">{l}<Tip text={SELL_HELP[k]} /></span>
                    <input value={form[k] ?? ""} onChange={(ev) => { set(k)(ev); setFErr((x) => ({ ...x, [k]: "" })); }} data-testid={`sell-field-${k}`} placeholder={SELL_PLACEHOLDER[k]}
                      inputMode={["prezzo_b2c", "prezzo_b2b_kg", "moq_kg", "peso_g", "spedizione_nazionale", "spedizione_europa", "spedizione_mondo"].includes(k) ? "decimal" : undefined}
                      className={"mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#d4af37]" + errCls(fErr[k])} />
                    <Help>{SELL_HELP[k]}</Help><FieldErr msg={fErr[k]} testid={`sell-error-${k}`} />
                  </label>
                ))}
              </div>
              <label className="block mt-4">
                <span className="text-xs text-zinc-500">Descrizione tecnica (Facoltativo)<Tip text="Testo descrittivo mostrato nella scheda prodotto: viene generato da '⚡ Compila Scheda Tecnica AI' ed è sempre modificabile." /></span>
                <textarea rows={3} value={form.descrizione} onChange={set("descrizione")} data-testid="sell-field-descrizione"
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#d4af37]" />
              </label>
              <label className="block mt-4">
                <span className="text-xs text-zinc-500">Proprietà riassuntive (Facoltativo)<Tip text="Riepilogo di formula, sistema, durezza e lucentezza mostrato nella scheda." /></span>
                <textarea rows={2} value={form.proprieta} onChange={set("proprieta")} data-testid="sell-field-proprieta"
                  className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#d4af37]" />
              </label>
              <div className="mt-4 grid sm:grid-cols-3 gap-4">
                {[["tr_it", "IT"], ["tr_en", "EN"], ["tr_zh", "ZH"]].map(([k, l]) => (
                  <label key={k} className="block">
                    <span className="text-xs text-zinc-500 flex items-center gap-1"><Globe2 className="w-3 h-3" />Traduzione {l}</span>
                    <textarea rows={3} value={form[k]} onChange={set(k)} data-testid={`sell-field-${k}`}
                      className="mt-1 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#10b981]" />
                  </label>
                ))}
              </div>
              <div className="mt-4 bg-black border border-[#10b981]/30 rounded-xl p-4">
                <span className="text-xs text-zinc-400 flex items-center gap-1.5 mb-2"><Video className="w-3.5 h-3.5 text-[#10b981]" />Video 360° del minerale (link YouTube / Reel / MP4)</span>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input value={form.video === DEMO_VIDEO ? "Video Cristallo Demo 360°" : form.video || ""} onChange={set("video")} placeholder="https://youtube.com/watch?v=..." data-testid="sell-field-video"
                      className="w-full bg-[#090a0f] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-[#10b981]" />
                  </div>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, video: DEMO_VIDEO }))} data-testid="use-demo-video-btn"
                    className="px-4 py-2 rounded-lg border border-[#10b981]/60 text-[#10b981] text-sm hover:bg-[#10b981]/10 transition-colors whitespace-nowrap">Usa Video Cristallo Demo</button>
                </div>
                {form.video && <div className="mt-3"><VideoPlayer url={form.video} poster={safePhotos.find(Boolean) || form.img || FALLBACK_IMG} /></div>}
              </div>
              <SellerBreakdown form={form} currency={currency} />
              {pubErr && <p className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg p-3" data-testid="publish-error">{pubErr}</p>}
              <p className="mt-4 text-[11px] text-zinc-500">* Obbligatori solo Nome, Prezzo e almeno una Foto. Tutti gli altri campi sono facoltativi.</p>
              <button onClick={publish} disabled={publishing} data-testid="publish-btn"
                className="mt-6 w-full py-4 rounded-full bg-[#d4af37] text-[#0f1115] font-semibold hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-shadow disabled:opacity-40">
                Pubblica sul Marketplace
              </button>
            </>
          )}
        </div>
      </div>
      <MyListings products={products} onDelete={onDelete} onEdit={onEdit} currency={currency} />
      <OffersPanel offers={offers} setOffers={setOffers} products={products} currency={currency} />
      {user?.is_admin && <LearnedReview />}
    </main>
  );
}

function SellerBreakdown({ form, currency }) {
  const p = Number(form.prezzo_b2c) || 0;
  const q = quote(p, Number(form.spedizione_nazionale) || 0, "bonifico");
  return (
    <div className="mt-4 grid sm:grid-cols-2 gap-4">
      <Breakdown testid="seller-net-breakdown" totalLabel="Ricavo Netto Venditore" total={fmt2(q.netto, currency)} rows={[
        ["Prezzo B2C", fmt2(p, currency)], ["Commissione KristallHub (5%, min 5 €)", `− ${fmt2(q.comm, currency)}`, "text-red-400"],
      ]} />
      <Breakdown testid="seller-buyer-preview" totalLabel="Totale Acquirente (es. Nazionale + Bonifico)" total={fmt2(q.totale, currency)} rows={[
        ["Prezzo", fmt2(p, currency)], ["Spedizione Nazionale", `+ ${fmt2(Number(form.spedizione_nazionale) || 0, currency)}`], ["Fee Escrow 3% (min 5 €)", `+ ${fmt2(q.fee, currency)}`],
      ]} />
    </div>
  );
}

function MyListings({ products, onDelete, onEdit, currency }) {
  const { user } = useAuth();
  const list = user?.is_admin ? products : products.filter((p) => p.venditore_id === user?.id);
  return (
    <section className="mt-12 bg-[#090a0f] border border-amber-500/20 rounded-2xl p-6" data-testid="my-listings">
      <h2 className="font-display text-3xl mb-6 flex items-center gap-3"><Store className="w-6 h-6 text-[#d4af37]" />{user?.is_admin ? "Tutti i Minerali (Moderazione Admin)" : "I tuoi Minerali"} <span className="text-base text-zinc-500">({list.length})</span></h2>
      {list.length === 0 && <p className="text-sm text-zinc-500">Nessun minerale pubblicato.</p>}
      <div className="space-y-2">
        {list.map((p) => (
          <div key={p.id} className="flex items-center gap-4 bg-black border border-white/5 rounded-xl p-3" data-testid={`listing-${p.id}`}>
            <Img src={p.img} alt={p.titolo} className="w-12 h-12 rounded-lg object-cover" />
            <div className="flex-1 min-w-0"><div className="truncate">{p.titolo}</div><div className="text-xs text-zinc-500">{sellerAlias(p)}{p.venditore_nome_admin ? ` (${p.venditore_nome_admin})` : ""} · {fmt(p.prezzo_b2c, currency)}</div></div>
            <button onClick={() => onEdit(p)} data-testid={`listing-edit-${p.id}`}
              className="px-3 py-1.5 rounded-lg bg-[#d4af37] text-black text-xs font-semibold hover:brightness-110 transition-[filter]">✏️ Modifica</button>
            <button onClick={() => onDelete(p)} data-testid={`listing-delete-${p.id}`}
              className="px-3 py-1.5 rounded-lg bg-red-600/90 text-white text-xs font-semibold hover:bg-red-600 transition-colors">{user?.is_admin ? "Elimina [Admin]" : "Elimina"}</button>
          </div>
        ))}
      </div>
    </section>
  );
}

const ORDER_STATI = { richiesta_ricevuta: "1 · Richiesta ricevuta", in_trattativa: "2 · In trattativa", confermato: "3 · Confermato", spedito: "4 · Spedito", completato: "5 · Completato" };
const ORDER_NEXT = { richiesta_ricevuta: ["in_trattativa", "Avvia Trattativa"], in_trattativa: ["confermato", "Conferma Ordine"], confermato: ["spedito", "Segna come Spedito"], spedito: ["completato", "Segna come Completato"] };

function TrackingBox({ o, onTracking }) {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = usePersist("kh_deliveries", {});
  const delivered = deliveries?.[o.id];
  const canConfirm = o.tracking && ["confermato", "spedito"].includes(o.stato) && o.acquirente?.id === user?.id && !delivered;
  const [corriere, setCorriere] = useState(o.tracking?.corriere || "");
  const [num, setNum] = useState(o.tracking?.tracking_number || "");
  const canEdit = ["confermato", "spedito"].includes(o.stato) && (user?.is_admin || o.venditore?.id === user?.id);
  return (
    <div className="mt-3" data-testid={`order-tracking-${o.id}`}>
      {o.tracking && (
        <div className="text-xs text-sky-300 flex items-center gap-1"><Package className="w-3.5 h-3.5" />
          <span>{o.tracking.corriere}: <a href={`https://t.17track.net/it#nums=${encodeURIComponent(o.tracking.tracking_number)}`} target="_blank" rel="noopener noreferrer"
            className="font-mono underline hover:text-sky-200" data-testid={`order-tracking-number-${o.id}`}>{o.tracking.tracking_number}</a></span>
        </div>
      )}
      {delivered && (
        <div className="mt-1 text-xs text-[#10b981]" data-testid={`order-delivered-${o.id}`}>
          Consegna confermata il {new Date(delivered).toLocaleString("it-IT")} · verifica 48h fino al {new Date(new Date(delivered).getTime() + 48 * 3600000).toLocaleString("it-IT")}
        </div>
      )}
      {canConfirm && (
        <button onClick={() => setDeliveries((d) => ({ ...(d || {}), [o.id]: new Date().toISOString() }))} data-testid={`order-confirm-delivery-${o.id}`}
          className="mt-2 px-3 py-1.5 rounded-lg bg-[#10b981] text-black text-xs font-semibold hover:brightness-110 transition-[filter]">Conferma Consegna (Avvia 48h)</button>
      )}
      {canEdit && (
        <div className="flex flex-wrap gap-2 mt-2">
          <input value={corriere} onChange={(e) => setCorriere(e.target.value)} placeholder="Corriere" data-testid={`tracking-carrier-${o.id}`}
            className="w-28 bg-[#090a0f] border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-sky-500" />
          <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="Tracking number" data-testid={`tracking-number-input-${o.id}`}
            className="w-36 bg-[#090a0f] border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-sky-500" />
          <button onClick={() => onTracking(o, corriere, num)} disabled={corriere.trim().length < 2 || num.trim().length < 4} data-testid={`tracking-save-${o.id}`}
            className="px-3 py-1.5 rounded-lg border border-sky-500/50 text-sky-400 text-xs hover:bg-sky-500/10 disabled:opacity-40 transition-colors">{o.tracking ? "Aggiorna Tracking" : "Salva Tracking"}</button>
        </div>
      )}
    </div>
  );
}

function OrderRow({ o, admin, currency, onAdvance, onTracking }) {
  const a = o.acquirente || {}, v = o.venditore || {};
  return (
    <div className="bg-black border border-amber-500/20 rounded-xl p-5 grid lg:grid-cols-[1.2fr_1fr_1fr_1fr] gap-5 text-sm" data-testid={`order-row-${o.id}`}>
      <div>
        <div className="font-mono text-[#d4af37]">{o.id}</div>
        <div className="font-medium mt-1">{o.mineral?.titolo}</div>
        <div className="text-xs text-zinc-500 mt-1">{new Date(o.created_at).toLocaleString("it-IT")} · {o.metodo_pagamento === "carta" ? "Carta" : "Bonifico"} · {SHIP[o.spedizione_fascia]}</div>
        <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full border ${o.stato === "completato" ? "border-[#10b981]/50 text-[#10b981]" : "border-[#d4af37]/40 text-[#d4af37]"}`} data-testid={`order-status-${o.id}`}>{ORDER_STATI[o.stato] || o.stato}</span>
        {admin && ORDER_NEXT[o.stato] && (
          <button onClick={() => onAdvance(o)} data-testid={`order-advance-${o.id}`}
            className="block mt-2 px-3 py-1.5 rounded-lg bg-[#10b981] text-black text-xs font-semibold hover:brightness-110 transition-[filter]">{ORDER_NEXT[o.stato][1]}</button>
        )}
        <TrackingBox o={o} onTracking={onTracking} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Acquirente</div>
        <div>{a.nome_ragione_sociale}</div>
        {a.email && <div className="text-xs text-zinc-400">{a.email}</div>}
        <div className="text-xs text-zinc-400">{a.indirizzo_completo} · {a.nazione}</div>
        {a.codice_fiscale && <div className="text-xs text-zinc-500">CF: {a.codice_fiscale}</div>}
        {a.partita_iva && <div className="text-xs text-zinc-500">P.IVA: {a.partita_iva}</div>}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Venditore</div>
        <div>{v.nome_ragione_sociale || v.codice}</div>
        <div className="text-xs text-zinc-400">{v.codice}{v.nazione ? ` · ${v.nazione}` : ""}</div>
        {v.email && <div className="text-xs text-zinc-400">{v.email}</div>}
        {v.partita_iva && <div className="text-xs text-zinc-500">P.IVA: {v.partita_iva}</div>}
      </div>
      <div className="space-y-1 text-xs">
        <div className="flex justify-between"><span className="text-zinc-500">Prezzo</span><span>{fmt2(o.prezzo_minerale, currency)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Spedizione</span><span>{fmt2(o.spedizione, currency)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Fee Escrow</span><span>{fmt2(o.fee_escrow, currency)}</span></div>
        <div className="flex justify-between font-semibold text-sm pt-1 border-t border-white/10"><span>{admin ? "Totale da incassare" : "Totale"}</span><span className="text-[#d4af37]">{fmt2(o.totale_acquirente, currency)}</span></div>
        <div className="flex justify-between text-[#10b981]"><span>{admin ? "Netto da saldare" : "Netto venditore"}</span><span>{fmt2(o.netto_venditore, currency)}</span></div>
      </div>
    </div>
  );
}

function OrdersView({ currency }) {
  const { user, token, openLogin } = useAuth();
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!token) return;
    apiFetch("/orders", { token }).then(setOrders).catch((e) => { setErr(e.message); setOrders([]); });
  }, [token]);
  const advance = async (o) => {
    try {
      const u = await apiFetch(`/orders/${o.id}/status`, { method: "PATCH", token, body: { stato: ORDER_NEXT[o.stato][0] } });
      setOrders((os) => os.map((x) => (x.id === o.id ? u : x)));
    } catch (e) { window.alert(e.message); }
  };
  const saveTracking = async (o, corriere, tracking_number) => {
    try {
      const u = await apiFetch(`/orders/${o.id}/tracking`, { method: "PATCH", token, body: { corriere, tracking_number } });
      setOrders((os) => os.map((x) => (x.id === o.id ? u : x)));
    } catch (e) { window.alert(e.message); }
  };
  if (!user) {
    return (
      <main className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="orders-locked">
        <Lock className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
        <button onClick={openLogin} className="px-6 py-3 rounded-full bg-[#d4af37] text-black font-semibold">Accedi per vedere i tuoi ordini</button>
      </main>
    );
  }
  const list = orders || [];
  const sum = (k) => list.reduce((s, o) => s + (Number(o[k]) || 0), 0);
  return (
    <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12" data-testid="orders-view">
      <p className="text-xs uppercase tracking-[0.3em] text-[#10b981] mb-3">{user.is_admin ? "Dashboard Admin · Escrow" : "Area Ordini"}</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-8">{user.is_admin ? "Tutti gli Ordini della Piattaforma" : "I miei Ordini"}</h1>
      {user.is_admin && (
        <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="admin-order-totals">
          {[["Ordini", list.length, "text-white"], ["Totale da incassare", fmt2(sum("totale_acquirente"), currency), "text-[#d4af37]"], ["Netto da saldare ai venditori", fmt2(sum("netto_venditore"), currency), "text-[#10b981]"]].map(([l, v, c]) => (
            <div key={l} className="bg-[#090a0f] border border-amber-500/20 rounded-2xl p-5"><div className="text-xs text-zinc-500">{l}</div><div className={`text-2xl font-semibold mt-1 ${c}`}>{v}</div></div>
          ))}
        </div>
      )}
      {err && <p className="text-sm text-red-400 mb-4" data-testid="orders-error">{err}</p>}
      {orders === null ? <p className="text-zinc-500">Caricamento...</p>
        : list.length === 0 ? <p className="text-zinc-500" data-testid="orders-empty">Nessun ordine ancora.</p>
          : <div className="space-y-3">{list.map((o) => <OrderRow key={o.id} o={o} admin={user.is_admin} currency={currency} onAdvance={advance} onTracking={saveTracking} />)}</div>}
    </main>
  );
}

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&family=Outfit:wght@300;400;500;600&display=swap');
.kh-root{font-family:'Outfit',sans-serif}
.font-display{font-family:'Cormorant Garamond',serif}
@keyframes khReveal{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
.kh-reveal{opacity:0;animation:khReveal .6s ease forwards}
@keyframes khScan{0%{top:0}50%{top:calc(100% - 4px)}100%{top:0}}
.kh-scanline{animation:khScan 1.5s ease-in-out infinite}
@keyframes khSpin{from{transform:rotateY(0)}to{transform:rotateY(360deg)}}
.kh-spin{animation:khSpin 6s linear infinite}
@keyframes khLight{0%{background-position:-150% 0}100%{background-position:250% 0}}
.kh-light{background:linear-gradient(110deg,transparent 40%,rgba(255,255,255,.12) 50%,transparent 60%);background-size:200% 100%;animation:khLight 3s linear infinite}
::selection{background:#d4af37;color:#0f1115}
@media print{body *{visibility:hidden!important}#kh-cert,#kh-cert *{visibility:visible!important}#kh-cert{position:fixed;left:0;top:0;width:100%;-webkit-print-color-adjust:exact;print-color-adjust:exact}.kh-noprint{display:none!important}}
`;

const ESCROW_INFO = [
  "Gestione via Email: KristallHub gestisce le richieste d'ordine e le trattative commerciali direttamente tra le parti via email, senza transazioni di denaro sul sito.",
  "1. Deposito in Cassaforte: L'acquirente versa l'importo totale (lotto + spedizione in base alla nazione di consegna) sul conto di garanzia KristallHub tramite Bonifico Bancario o Link Carta Sicuro inviato da info@kristallhub.com. Ordine minimo: 50 €.",
  "2. Spedizione Protetta: KristallHub verifica i fondi e autorizza il venditore a spedire con corriere tracciato e assicurato.",
  "3. Sblocco Fondi: Entro 48 ore dalla consegna senza reclami video, KristallHub sblocca il pagamento al venditore.",
  "Tariffe Trasparenti: Solo 5% di commissione per il Venditore (minimo 5 €). Per l'Acquirente, la quota di Protezione Escrow è solo del 3% pagando con Bonifico Bancario o 5% con Carta di Credito/Stripe (minimo 5 €).",
];
const SHIPPING_INFO = [
  "Costi di Spedizione per Nazione: La spedizione tracciata e assicurata è obbligatoria, gestita dal venditore e a carico dell'acquirente secondo 3 fasce (Nazionale, Europa, Resto del Mondo) indicate nell'annuncio o calcolate alla conferma d'ordine. Eventuali dazi doganali extra-UE sono sempre a carico dell'acquirente.",
  "Prova Video Obbligatoria (Unboxing): Per segnalare danni da trasporto o merce non conforme, l'acquirente DEVE inviare a info@kristallhub.com un video continuo e senza tagli dell'apertura del pacco sigillato entro 48 ore dalla consegna. Senza video, il reclamo è nullo e i fondi vengono sbloccati.",
  "Esclusione Resi: Transazioni globali protette da Escrow. Non sono ammessi resi per ripensamento o per mancato ritiro doganale.",
];

function InfoBox({ title, items, icon: I, accent, testid }) {
  return (
    <div className={`bg-[#090a0f] border rounded-2xl p-6 ${accent}`} data-testid={testid}>
      <h3 className="font-display text-2xl mb-4 flex items-center gap-2"><I className="w-5 h-5" />{title}</h3>
      <ul className="space-y-3 text-sm text-zinc-300 leading-relaxed">
        {items.map((t) => {
          const [head, ...rest] = t.split(": ");
          return <li key={head}>- <span className="font-semibold text-white">{head}:</span> {rest.join(": ")}</li>;
        })}
      </ul>
    </div>
  );
}

function App() {
  const [products, setProducts] = useState(SEED_PRODUCTS);
  const [token, setToken] = usePersist("kh_token", "");
  const [user, setUser] = useState(null);
  const [chats, setChats] = usePersist("kh_chats", {});
  const [rawOffers, setRawOffers] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [learnedPending, setLearnedPending] = useState(0);
  const [notes, setNotes] = useState([]);
  const [editP, setEditP] = useState(null);
  const [seenShip, setSeenShip] = usePersist("kh_seen_tracking", []);
  const [auctions, setAuctions] = usePersist("kh_auctions", seedAuctions());
  const [favs, setFavs] = usePersist("kh_favs", []);
  const [onlyFavs, setOnlyFavs] = useState(false);
  const [role, setRoleRaw] = usePersist("kh_role", "b2c");
  const [mode, setMode] = usePersist("kh_mode", "b2c");
  const [currency, setCurrency] = usePersist("kh_currency", "EUR");
  const [view, setView] = useState("catalog");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [certProduct, setCertProduct] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const safeOffers = useMemo(() => rawOffers.map((o) => fromOffer(o, user?.id)), [rawOffers, user]);
  const setOffers = setRawOffers;

  const safeRole = ROLES[role] ? role : "b2c";
  const setRole = (r) => { setRoleRaw(r); setMode(ROLES[r].mode); };
  const safeProducts = useMemo(() => (Array.isArray(products) && products.length ? products : SEED_PRODUCTS), [products]);
  const safeFavs = Array.isArray(favs) ? favs : [];
  const safeAuctions = auctions && typeof auctions === "object" && !Array.isArray(auctions) ? auctions : seedAuctions();
  const toggleFav = (id) => setFavs((f) => (Array.isArray(f) && f.includes(id) ? f.filter((x) => x !== id) : [...(Array.isArray(f) ? f : []), id]));
  useEffect(() => {
    setAuctions((as) => {
      const base = as && typeof as === "object" && !Array.isArray(as) ? as : seedAuctions();
      return Object.fromEntries(Object.entries(base).map(([k, a]) => [k, a.endsAt < Date.now() ? { ...a, endsAt: Date.now() + 24 * H } : a]));
    });
  }, [setAuctions]);
  useEffect(() => {
    apiFetch("/minerals", { token }).then((list) => { if (Array.isArray(list) && list.length) setProducts(list.map(fromApi)); }).catch(() => {});
  }, [token]);
  useEffect(() => {
    if (!token) { setUser(null); return; }
    apiFetch("/auth/me", { token }).then(setUser).catch(() => { setToken(""); setUser(null); });
  }, [token, setToken]);
  useEffect(() => {
    if (!user) return;
    const r = buyerRoleOf(user);
    setRoleRaw(r); setMode(ROLES[r].mode);
  }, [user, setRoleRaw, setMode]);
  const logout = () => { setToken(""); setUser(null); setRawOffers([]); setRoleRaw("b2c"); setMode("b2c"); };
  const loadOffers = () => {
    if (!token) { setRawOffers([]); setMyOrders([]); return; }
    apiFetch("/offers", { token }).then(setRawOffers).catch(() => {});
    apiFetch("/orders", { token }).then(setMyOrders).catch(() => {});
    if (user?.is_admin) apiFetch("/admin/learned", { token }).then((l) => setLearnedPending(l.filter((d) => d.stato !== "approvato").length)).catch(() => {});
    else setLearnedPending(0);
    apiFetch("/notifications", { token }).then(setNotes).catch(() => {});
  };
  const readNotes = () => {
    setNotes((ns) => ns.map((n) => ({ ...n, read: true })));
    apiFetch("/notifications/read", { method: "POST", token }).catch(() => {});
  };
  const shipments = myOrders.filter((o) => o.tracking && o.acquirente?.id === user?.id);
  useEffect(() => {
    loadOffers();
    if (!token) return undefined;
    const t = setInterval(loadOffers, 15000);
    return () => clearInterval(t);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const createOffer = async (mineral_id, amount) => {
    const o = await apiFetch("/offers", { method: "POST", token, body: { mineral_id, amount } });
    setRawOffers((os) => [...os, o]);
  };
  const actOffer = async (id, azione, counter) => {
    try {
      const o = await apiFetch(`/offers/${id}`, { method: "PATCH", token, body: { azione, counter: counter ?? null } });
      setRawOffers((os) => os.map((x) => (x.id === id ? o : x)));
    } catch (e) { window.alert(e.message); }
  };
  const markRead = () => {
    setRawOffers((os) => os.map((o) => (o.buyer_id === user?.id ? { ...o, unread_buyer: false } : o)));
    apiFetch("/offers/read", { method: "POST", token }).catch(() => {});
  };
  const publishMineral = async (p) => {
    const m = await apiFetch("/minerals", { method: "POST", token, body: toApi(p) });
    setProducts((ps) => [fromApi(m), ...ps]);
    return m;
  };
  const deleteMineral = async (p) => {
    if (!window.confirm(`Eliminare definitivamente "${p.titolo}"?`)) return;
    try {
      await apiFetch(`/products/${p.id}`, { method: "DELETE", token });
      setProducts((ps) => ps.filter((x) => x.id !== p.id));
      setSelected(null);
    } catch (e) { window.alert(e.message); }
  };
  const saveEdit = async (p, f) => {
    const m = await apiFetch(`/products/${p.id}`, { method: "PUT", token, body: toApi({ ...p, ...f }) });
    const np = fromApi(m);
    setProducts((ps) => ps.map((x) => (x.id === p.id ? np : x)));
    setSelected((s) => (s && s.id === p.id ? np : s));
    setEditP(null);
  };
  const authValue = { user, setUser, token, openLogin: () => setLoginOpen(true), logout, createOffer, actOffer };

  return (
    <AuthCtx.Provider value={authValue}>
    <div className="kh-root min-h-screen bg-black text-zinc-100 overflow-x-hidden max-w-full">
      <style>{STYLES}</style>
      <Header view={view} setView={setView} search={search} setSearch={setSearch} currency={currency} setCurrency={setCurrency}
        role={safeRole} setRole={setRole} mode={mode} setMode={setMode} openLogin={() => setLoginOpen(true)}
        favCount={safeFavs.length} onlyFavs={onlyFavs} setOnlyFavs={setOnlyFavs}
        bell={<NotificationBell offers={safeOffers} products={safeProducts} currency={currency} onRead={markRead} shipments={shipments} seenShip={Array.isArray(seenShip) ? seenShip : []} onSeenShip={setSeenShip} adminPending={learnedPending} onReview={() => { setView("sell"); setTimeout(() => document.querySelector('[data-testid="learned-review-panel"]')?.scrollIntoView({ behavior: "smooth" }), 300); }} onPay={(product, amount, offerId) => setPayTarget({ product, amount, offerId })} notes={Array.isArray(notes) ? notes : []} onReadNotes={readNotes} />} />
      {view === "catalog" && <Catalog products={safeProducts} mode={mode} currency={currency} search={search} onOpen={setSelected} favs={safeFavs} toggleFav={toggleFav} onlyFavs={onlyFavs} onCert={setCertProduct} onDelete={deleteMineral} onEdit={setEditP} />}
      {editP && <EditMineralModal product={editP} onClose={() => setEditP(null)} onSave={saveEdit} />}
      {view === "orders" && <OrdersView currency={currency} />}
      {view === "auctions" && <Auctions products={safeProducts} auctions={safeAuctions} setAuctions={setAuctions} currency={currency} onOpen={setSelected} favs={safeFavs} toggleFav={toggleFav} />}
      {view === "sell" && <SellDashboard openLogin={() => setLoginOpen(true)} offers={safeOffers} setOffers={setOffers} products={safeProducts} currency={currency}
        onPublish={publishMineral} onDelete={deleteMineral} onEdit={setEditP} />}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pb-12 grid lg:grid-cols-2 gap-6" data-testid="info-section">
        <InfoBox title="Come funziona il Pagamento Sicuro (Escrow) e Tariffe" items={ESCROW_INFO} icon={Lock} accent="border-[#10b981]/40" testid="escrow-info-box" />
        <InfoBox title="Regole Spedizioni Internazionali, Dogane, Danni e Resi" items={SHIPPING_INFO} icon={Package} accent="border-[#d4af37]/40" testid="shipping-rules-box" />
      </section>
      <footer className="border-t border-white/5 py-10 text-center text-xs text-zinc-600">KristallHub © 2026 · Marketplace Globale B2B & B2C di Minerali · Protetto da Escrow</footer>
      {selected && <DetailModal product={selected} mode={mode} currency={currency} role={safeRole} onClose={() => setSelected(null)}
        chats={chats} setChats={setChats} offers={safeOffers} setOffers={setOffers} onCert={setCertProduct} onEdit={setEditP} onDelete={deleteMineral} />}
      {certProduct && <CertificateModal product={certProduct} onClose={() => setCertProduct(null)} />}
      {payTarget && <Checkout product={payTarget.product} amount={payTarget.amount} offerId={payTarget.offerId} mode={mode} currency={currency} onClose={() => { setPayTarget(null); loadOffers(); }} />}
      {loginOpen && <AuthModal onClose={() => setLoginOpen(false)} onAuth={(t, u) => { setToken(t); setUser(u); setLoginOpen(false); }} onGuest={(r) => { setRole(r); setLoginOpen(false); }} />}
    </div>
    </AuthCtx.Provider>
  );
}

export default App;
